/* ═══════════════════════════════════════════════════════════════════════════
   CUURT MUUV — SIZE FINDER v3  (never-short engine)
   ───────────────────────────────────────────────────────────────────────────
   CORE PRINCIPLE (non-negotiable):
     The recommended CUURT size must have an internal (insole) length >= the
     customer's required length. Equal or slightly longer is fine.
     SHORTER IS NEVER ACCEPTABLE. Every tie rounds UP.

     A shoe that arrives a few mm long is a minor annoyance.
     A shoe that arrives short is a return. Every tie is biased toward
     "not short."

   Changes vs v2:
   • Engine rebuilt around a ceiling function on millimetres of internal
     length. v2 used NEAREST match, which selected a size shorter than the
     customer's current insole in 71 of 152 brand/size combinations (47%).
     That single behaviour was the largest mechanical driver of fit returns.
   • Every recommendation now funnels through ONE primitive, ceilingSize(),
     so "never shorter than required" is a property of the code, not a promise.
   • Width is capped at +1 size, with two anti-stacking guards so a width bump
     is never added on top of a length round-up.
   • Foot conditions no longer change the size at all — they only change the
     copy. Length is a poor proxy for width and a bad proxy for toe shape.
   • Ladder-slope correction guards against the stretched competitor tables
     found in the chart audit (see AUDIT NOTES below).
   • Result screen rewritten: states the size, explains the rule in one line,
     and ties the customer's selected condition to a concrete CUURT benefit.
     No medical claims anywhere.

   AUDIT NOTES on the data below (full detail in the accompanying report):
   • Wilson, Skechers, Babolat, K-Swiss, New Balance and 'Other' step ~10 mm
     per full US size instead of the true 8.47 mm — a ~18% stretch. They are
     accurate near EU 42 and drift both ways from there. Below EU 42 they
     UNDERSTATE the real insole and would ship a short shoe.
     LADDER_CORRECTION_ENABLED guards against this until real insoles are
     caliper-measured.
   • Nike, Adidas and Asics ladders check out and are never corrected.
   • K-Swiss EU label '37.7' is a typo for 37.5 — CORRECTED below.
   • Skechers has no women's EU table; women fall back to the men's ladder.
   • CUURT's own US columns do not sit on the standard US ladder (EU 42 is
     labelled US 8-8.5 but measures US ~8.9). EU is the reliable label.
   ═══════════════════════════════════════════════════════════════════════════ */
(function () {

  /* ── CUURT source of truth — internal last, mm (unisex) ─────────────────
     EU IS THE GROUND TRUTH. The engine works in millimetres and resolves to an
     EU size; usM / usW / uk are display labels only, and they are transcribed
     verbatim from the published CUURT size chart. They are NOT derived and must
     not be recomputed — an earlier version tried, and it disagreed with both the
     chart and the live Shopify variants.

     Ranges ("6-6.5") are deliberate: the CUURT ladder steps 6.6 mm while US
     steps 8.47 mm, so one CUURT size legitimately covers more than one US size
     at some points on the ladder.

     Women's stops at EU 45 — the site lists no women's equivalent above it,
     matching the Shopify variants, which are men-only for EU 46/47/48.

     ONE DELIBERATE DEPARTURE from the published chart: EU 44 men's reads
     "10-10.5", not "10". The live Shopify variant is "US Women 11/11.5 - Men
     10/10.5", and the store wins — a US M 10.5 customer must not read the chart,
     find nothing for them, and leave. Every other row matches the chart exactly.
     ─────────────────────────────────────────────────────────────────────── */
  var CUURT = [
    { eu: 36, len: 233.6, wid: 89.52, usM: '4.5', usW: '5.5', uk: '3.5' },
    { eu: 37, len: 240.2, wid: 91.10, usM: '5', usW: '6', uk: '4' },
    { eu: 38, len: 246.8, wid: 92.68, usM: '5.5', usW: '6.5', uk: '5' },
    { eu: 39, len: 253.4, wid: 94.26, usM: '6-6.5', usW: '7-7.5', uk: '6' },
    { eu: 40, len: 260.0, wid: 95.84, usM: '7', usW: '8-8.5', uk: '6.5' },
    { eu: 41, len: 266.6, wid: 97.42, usM: '7.5', usW: '9-9.5', uk: '7' },
    { eu: 42, len: 273.2, wid: 99.00, usM: '8-8.5', usW: '10', uk: '8' },
    { eu: 43, len: 279.8, wid: 100.58, usM: '9-9.5', usW: '10.5', uk: '9' },
    { eu: 44, len: 286.4, wid: 102.16, usM: '10-10.5', usW: '11-11.5', uk: '9.5' },
    { eu: 45, len: 293.0, wid: 103.74, usM: '11-11.5', usW: '12', uk: '10/10.5' },
    { eu: 46, len: 299.6, wid: 105.32, usM: '12', usW: '—', uk: '11' },
    { eu: 47, len: 306.2, wid: 106.90, usM: '13', usW: '—', uk: '12' },
    { eu: 48, len: 312.8, wid: 108.48, usM: '14', usW: '—', uk: '12.5/13' }
  ];

  /* ── Brand tables — US size → INSOLE/INTERNAL length (mm) ────
     Basis change (insole document): all brand values converted to
     internal/insole length, same basis as CUURT's last-length column.
     Offsets applied to declared chart values: +12 mm Nike/Adidas &
     generic 'Other' US (charts declare foot length), +6 mm Wilson/
     Skechers/Babolat/K-Swiss/New Balance (charts declare CM/JP shoe
     measure), +8 mm Asics. Anchored by a physical Nike EU 42 insole
     measurement (270 mm); derived values carry ±2–3 mm uncertainty
     until competitor insoles are caliper-measured directly.
     Original note — US size → foot length the size is cut for.
     Values transcribed from official brand charts (see source chats).
     Tables marked DERIVED use the standard men↔women +1.5 US offset
     applied to the official chart of the other gender. ─────────── */
  var BD = {
    'Nike': { /* official chart */
      men:   {3.5:228,4:232,4.5:236,5:241,5.5:245,6:249,6.5:253,7:257,7.5:262,8:266,8.5:270,9:274,9.5:279,10:283,10.5:287,11:291,11.5:295,12:300,12.5:304,13:308,13.5:312,14:317},
      women: {5:228,5.5:232,6:236,6.5:241,7:245,7.5:249,8:253,8.5:257,9:262,9.5:266,10:270,10.5:274,11:279,11.5:283,12:287,12.5:291,13:295,13.5:300,14:304,14.5:308,15:312,15.5:317}
    },
    'Adidas': { /* official chart (Adidas uses a 1.0 M/W offset) */
      men:   {4:233,4.5:237,5:241,5.5:245,6:250,6.5:254,7:258,7.5:262,8:267,8.5:271,9:275,9.5:279,10:283,10.5:288,11:292,11.5:296,12:300,12.5:305,13:309,13.5:313,14:317,14.5:322},
      women: {5:233,5.5:237,6:241,6.5:245,7:250,7.5:254,8:258,8.5:262,9:267,9.5:271,10:275,10.5:279,11:283,11.5:288,12:292,12.5:296,13:300,13.5:305,14:309,14.5:313,15:317}
    },
    'Wilson': { /* official chart; women DERIVED (+1.5, unisex EU last) */
      men:   {4:226,4.5:231,5:236,5.5:241,6:246,6.5:251,7:256,7.5:261,8:266,8.5:271,9:276,9.5:281,10:286,10.5:291,11:296,11.5:301,12:306,12.5:311,13:316,14:326},
      women: {5.5:226,6:231,6.5:236,7:241,7.5:246,8:251,8.5:256,9:261,9.5:266,10:271,10.5:276,11:281,11.5:286,12:291,12.5:296,13:301,13.5:306,14:311,14.5:316,15.5:326}
    },
    'Skechers': { /* men official; women DERIVED (v1 was ~30 mm too large) */
      men:   {6.5:251,7:256,7.5:261,8:266,8.5:271,9:276,9.5:281,10:286,10.5:291,11:296,11.5:301,12:306,12.5:311,13:316,13.5:321,14:326,15:336,16:346},
      women: {5:221,5.5:226,6:231,6.5:236,7:241,7.5:246,8:251,8.5:256,9:261,9.5:266,10:271,10.5:276,11:281,11.5:286,12:291}
    },
    'Babolat': { /* official charts, men + women */
      men:   {6.5:251,7:256,7.5:261,8:266,8.5:271,9:276,9.5:281,10:286,10.5:291,11:296,11.5:301,12:306,12.5:311,13:316,14:326},
      women: {5:231,6:236,6.5:241,7:246,7.5:251,8:256,8.5:261,9:266,9.5:271,10:276,10.5:281}
    },
    'Asics': { /* OFFICIAL men's chart (UK/EU/CM/US), supplied 2026. CM is ASICS'
       declared foot length; insole = CM + 6 mm, the same conversion used for every
       other CM-declaring brand here. The +6 was independently confirmed by a
       least-squares fit against the physically-anchored Nike/Adidas ladders
       (best fit +5.6 mm, RMS 1.7 mm at +6). The previous table used +8 mm and
       carried transcription errors of up to 4.5 mm. Women = men + 1.5 US. */
      men:   {3.5:228.5,4:231,4.5:236,5:241,5.5:246,6:251,6.5:256,7:258.5,7.5:261,8:266,8.5:271,9:276,9.5:281,10:286,10.5:288.5,11:291,11.5:296,12:301,12.5:306,13:311,14:316,15:326,16:336},
      women: {5:228.5,5.5:231,6:236,6.5:241,7:246,7.5:251,8:256,8.5:258.5,9:261,9.5:266,10:271,10.5:276,11:281,11.5:286,12:288.5,12.5:291,13:296,13.5:301,14:306,14.5:311,15.5:316,16.5:326,17.5:336}
    },
    'K-Swiss': { /* official charts, men + women */
      men:   {5:236,5.5:241,6:246,6.5:251,7:256,7.5:261,8:266,8.5:271,9:276,9.5:281,10:286,10.5:291,11:296,11.5:301,12:306,12.5:311,13:316,14:326,15:331},
      women: {4.5:221,5:226,5.5:231,6:236,6.5:241,7:246,7.5:251,8:256,8.5:261,9:266,9.5:271,10:276,10.5:281,11:286,12:296}
    },
    'New Balance': { /* official NB chart (v1 JS used generic values) */
      men:   {4:226,4.5:231,5:236,5.5:241,6:246,6.5:251,7:256,7.5:261,8:266,8.5:271,9:276,9.5:281,10:286,10.5:291,11:296,11.5:301,12:306,12.5:311,13:316,13.5:321,14:326},
      women: {4:211,4.5:216,5:221,5.5:226,6:231,6.5:236,7:241,7.5:246,8:251,8.5:256,9:261,9.5:266,10:271,10.5:276,11:281,11.5:286,12:291,12.5:296,13:301,13.5:306,14:311}
    },
    'Other': { /* generic US standard */
      men:   {4:232,4.5:236,5:241,5.5:245,6:249,6.5:253,7:258,7.5:262,8:266,8.5:270,9:275,9.5:279,10:283,10.5:287,11:291,11.5:295,12:300,12.5:304,13:308,14:317},
      women: {4.5:227,5:232,5.5:236,6:240,6.5:244,7:249,7.5:253,8:257,8.5:261,9:266,9.5:270,10:274,10.5:279,11:283,11.5:287,12:291,12.5:296,13:300}
    }
  };


  /* ── EU-anchored tables — EU label → INTERNAL/INSOLE length (mm) ──
     NOTE: these hold INSOLE lengths, same basis as CUURT above. The old
     "foot length" label on this block was stale and misleading. ────────
     From the verified pixel-by-pixel chart reads. European
     customers know their EU size; this skips US conversion.
     Format: [label shown on the box, numeric value, mm] ───── */
  var EU_WILSON = [ /* Wilson official EU chart — 22.0–32.0 cm, ISO fractional labels */
    ['36',36,226],['36\u2154',36.67,231],['37\u2153',37.33,236],['38',38,241],['38\u2154',38.67,246],
    ['39\u2153',39.33,251],['40',40,256],['40\u2154',40.67,261],['41\u2153',41.33,266],['42',42,271],
    ['42\u2154',42.67,276],['43\u2153',43.33,281],['44',44,286],['44\u2154',44.67,291],['45\u2153',45.33,296],
    ['46',46,301],['46\u2154',46.67,306],['47\u2153',47.33,311],['48',48,316],['49\u2153',49.33,326]
  ];
  var EU_ADIDAS = [ /* Adidas official EU chart — its own foot-cm scale, NOT the same
     as Wilson’s despite matching ISO fractional labels (e.g. EU42: Wilson=265mm, Adidas=259mm).
     Adidas also publishes further into large sizes than Wilson/CUURT (up to EU 55⅔) — kept
     here so an Adidas wearer with a large foot still gets an accurate honest read, even
     though the recommendation engine will correctly cap out at CUURT's EU 48 ceiling. */
    ['36',36,233],['36\u2154',36.67,237],['37\u2153',37.33,241],['38',38,245],['38\u2154',38.67,250],
    ['39\u2153',39.33,254],['40',40,258],['40\u2154',40.67,262],['41\u2153',41.33,267],['42',42,271],
    ['42\u2154',42.67,275],['43\u2153',43.33,279],['44',44,283],['44\u2154',44.67,288],['45\u2153',45.33,292],
    ['46',46,296],['46\u2154',46.67,300],['47\u2153',47.33,305],['48',48,309],['48\u2154',48.67,313],
    ['49\u2153',49.33,317],['50',50,322],['50\u2154',50.67,326],['51\u2153',51.33,330],['52\u2154',52.67,338],
    ['53\u2153',53.33,347],['54\u2154',54.67,355],['55\u2154',55.67,364]
  ];
  var BDEU = {
    'Wilson': { u: EU_WILSON },
    'Adidas': { u: EU_ADIDAS },
    'Nike': { u: [ /* half-integer chart (as printed on boxes) */
      ['35.5',35.5,228],['36',36,232],['36.5',36.5,236],['37.5',37.5,241],['38',38,245],['38.5',38.5,249],
      ['39',39,253],['40',40,257],['40.5',40.5,262],['41',41,266],['42',42,270],['42.5',42.5,274],
      ['43',43,279],['44',44,283],['44.5',44.5,287],['45',45,291],['45.5',45.5,295],['46',46,300],
      ['47',47,304],['47.5',47.5,308],['48',48,312],['48.5',48.5,317]
    ] },
    'Skechers': { u: [ /* men's chart; Skechers women's EU chart not published */
      ['39',39,251],['39.5',39.5,256],['40',40,261],['41',41,266],['41.5',41.5,271],['42',42,276],
      ['42.5',42.5,281],['43',43,286],['44',44,291],['45',45,296],['45.5',45.5,301],['46',46,306],
      ['47',47,311],['47.5',47.5,316],['48',48,321],['48.5',48.5,326]
    ] },
    'Babolat': {
      men: [
        ['39',39,251],['40',40,256],['40.5',40.5,261],['41',41,266],['42',42,271],['42.5',42.5,276],
        ['43',43,281],['44',44,286],['44.5',44.5,291],['45',45,296],['46',46,301],['46.5',46.5,306],
        ['47',47,311],['48',48,316],['49',49,326]
      ],
      women: [
        ['36',36,231],['36.5',36.5,236],['37',37,241],['38',38,246],['38.5',38.5,251],['39',39,256],
        ['40',40,261],['40.5',40.5,266],['41',41,271],['42',42,276],['42.5',42.5,281]
      ]
    },
    'Asics': { u: [ /* OFFICIAL chart. Row = [label, EU numeric, insole mm, UK size].
       ASICS EU sizing is the same for men and women; only the US label differs. */
      ['35.5',35.5,228.5,2.5],['36',36,231,3],['37',37,236,3.5],['37.5',37.5,241,4],['38',38,246,4.5],
      ['39',39,251,5],['39.5',39.5,256,5.5],['40',40,258.5,6],['40.5',40.5,261,6.5],['41.5',41.5,266,7],
      ['42',42,271,7.5],['42.5',42.5,276,8],['43.5',43.5,281,8.5],['44',44,286,9],['44.5',44.5,288.5,9.5],
      ['45',45,291,10],['46',46,296,10.5],['46.5',46.5,301,11],['47',47,306,11.5],['48',48,311,12],
      ['49',49,316,13],['50.5',50.5,326,14],['51.5',51.5,336,15]
    ] },
    'K-Swiss': {
      men: [
        ['37.5',37.5,236],['38',38,241],['38.5',38.5,246],['39',39,251],['39.5',39.5,256],['40',40,261],
        ['41',41,266],['41.5',41.5,271],['42',42,276],['42.5',42.5,281],['43',43,286],['44',44,291],
        ['44.5',44.5,296],['45',45,301],['46',46,306],['46.5',46.5,311],['47',47,316],['49',49,326],['50',50,331]
      ],
      women: [
        ['35',35,221],['35.5',35.5,226],['36',36,231],['37',37,236],['37.5',37.5,241],['38',38,246],
        ['39',39,251],['39.5',39.5,256],['40',40,261],['41',41,266],['41.5',41.5,271],['42',42,276],
        ['42.5',42.5,281],['43',43,286],['44.5',44.5,296]
      ]
    },
    'New Balance': { u: [
      ['34',34,211],['35',35,216],['35.5',35.5,221],['36',36,226],['37',37,231],['37.5',37.5,236],
      ['38',38,241],['38.5',38.5,246],['39.5',39.5,251],['40',40,256],['40.5',40.5,261],['41.5',41.5,266],
      ['42',42,271],['42.5',42.5,276],['43',43,281],['44',44,286],['44.5',44.5,291],['45',45,296],
      ['45.5',45.5,301],['46.5',46.5,306],['47',47,311],['47.5',47.5,316],['48.5',48.5,321],['49',49,326]
    ] },
    'Other': { u: [ /* ISO standard (matches Wilson/Adidas), integer labels */
      ['36',36,226],['37',37,233.5],['38',38,241],['39',39,248.5],['40',40,256],['41',41,263.5],
      ['42',42,271],['43',43,278.5],['44',44,286],['45',45,293.5],['46',46,301],['47',47,308.5],['48',48,316]
    ] }
  };
  function euList(brand, gender) {
    var t = BDEU[brand] || BDEU['Other'];
    if (t.u) return t.u;
    return t[gender] || t.men || t.women;
  }

  /* Modal markup, generated by build.js from demo.html — do not hand-edit.
     Injected into <body> at load only if the page has no #cuurt-size-root. */
  var CUURT_MODAL_HTML = "<div id=\"cuurt-size-root\">\n\n<style>\n  #cuurtScPanel { transform: translateX(100%); transition: transform 0.35s cubic-bezier(0.22,1,0.36,1); }\n  #cuurtScPanel.cuurt-panel-open { transform: translateX(0); }\n  @media (max-width: 640px) {\n    #cuurtScPanel { width: 100% !important; }\n    #cuurtQuizModal { max-width: 100% !important; max-height: 100vh !important; border-radius: 0 !important; margin: 0 !important; height: 100vh !important; }\n  }\n</style>\n\n<!-- PANEL OVERLAY -->\n<div id=\"cuurtScOverlay\" onclick=\"cuurtClosePanel()\" style=\"display:none;position:fixed;inset:0;background:rgba(0,0,0,0.45);z-index:9998;\"></div>\n\n<!-- PANEL -->\n<div id=\"cuurtScPanel\" style=\"display:none;position:fixed;top:0;right:0;width:min(480px,100vw);height:100vh;background:white;z-index:9999;box-shadow:0 20px 60px rgba(0,60,95,0.18);flex-direction:column;overflow:hidden;\">\n\n  <div style=\"display:flex;align-items:center;justify-content:space-between;padding:20px 24px;border-bottom:1px solid #e2e8f0;flex-shrink:0;\">\n    <span style=\"font-size:20px;font-weight:700;color:#003C5F;letter-spacing:0.5px;text-transform:uppercase;\">Size &amp; Fit Chart</span>\n    <button onclick=\"cuurtClosePanel()\" type=\"button\" style=\"background:none;border:none;cursor:pointer;padding:6px;color:#6b7280;border-radius:50%;display:flex;align-items:center;justify-content:center;\">\n      <svg width=\"20\" height=\"20\" viewBox=\"0 0 24 24\" fill=\"none\" stroke=\"currentColor\" stroke-width=\"2.5\"><path d=\"M18 6L6 18M6 6l12 12\"/></svg>\n    </button>\n  </div>\n\n  <div style=\"flex:1;overflow-y:auto;padding:24px;\">\n    <button onclick=\"cuurtOpenQuiz()\" type=\"button\" style=\"width:100%;padding:14px 20px;background:transparent;border:1.5px solid #003C5F;border-radius:12px;font-size:15px;font-weight:600;color:#003C5F;cursor:pointer;display:flex;align-items:center;justify-content:center;gap:8px;margin-bottom:24px;font-family:inherit;\">\n      <svg width=\"18\" height=\"18\" viewBox=\"0 0 24 24\" fill=\"none\" stroke=\"currentColor\" stroke-width=\"2\"><circle cx=\"11\" cy=\"11\" r=\"8\"/><path d=\"m21 21-4.35-4.35M11 8v6M8 11h6\"/></svg>\n      Find my Perfect Size\n    </button>\n\n    <p style=\"font-size:14px;color:#6b7280;line-height:1.6;margin-bottom:20px;\"><strong style=\"color:#1a1a2e;\">CUURT Muuv is unisex</strong> — men and women share the same sizing.</p>\n\n    <div style=\"display:flex;border:1px solid #e2e8f0;border-radius:8px;overflow:hidden;margin-bottom:18px;\"><button id=\"cuurtTabMen\" onclick=\"cuurtShowTable('men')\" type=\"button\" style=\"flex:1;padding:10px;background:#003C5F;border:none;cursor:pointer;font-size:12.5px;font-weight:600;color:white;text-transform:uppercase;letter-spacing:0.8px;font-family:inherit;\">MEN</button><button id=\"cuurtTabWomen\" onclick=\"cuurtShowTable('women')\" type=\"button\" style=\"flex:1;padding:10px;background:#f4f6f8;border:none;cursor:pointer;font-size:12.5px;font-weight:600;color:#6b7280;text-transform:uppercase;letter-spacing:0.8px;font-family:inherit;\">WOMEN</button><button id=\"cuurtTabUk\" onclick=\"cuurtShowTable('uk')\" type=\"button\" style=\"flex:1;padding:10px;background:#f4f6f8;border:none;cursor:pointer;font-size:12.5px;font-weight:600;color:#6b7280;text-transform:uppercase;letter-spacing:0.8px;font-family:inherit;\">UK</button></div>\n<!-- The three size tables are RENDERED FROM THE ENGINE DATA at load, not\n     hand-written here. That is deliberate: a hand-copied chart can drift\n     from the numbers the recommendation is computed with, and a customer\n     comparing the two would be right to distrust both. -->\n<div id=\"cuurtTableMen\"   style=\"display:block;\"></div>\n<div id=\"cuurtTableWomen\" style=\"display:none;\"></div>\n<div id=\"cuurtTableUk\"    style=\"display:none;\"></div>\n  </div>\n</div>\n\n<!-- QUIZ OVERLAY -->\n<div id=\"cuurtQuizOverlay\" style=\"display:none;position:fixed;inset:0;background:rgba(0,0,0,0.55);z-index:10000;align-items:center;justify-content:center;\">\n  <div id=\"cuurtQuizModal\" style=\"background:white;width:100%;max-width:520px;max-height:92vh;border-radius:20px;overflow:hidden;display:flex;flex-direction:column;box-shadow:0 30px 80px rgba(0,60,95,0.3);margin:16px;\">\n\n    <div style=\"background:#003C5F;padding:20px 20px 16px;flex-shrink:0;\">\n      <div style=\"display:flex;align-items:flex-start;justify-content:space-between;margin-bottom:16px;\">\n        <div style=\"display:flex;align-items:center;gap:12px;\">\n          <div style=\"width:44px;height:44px;background:rgba(255,255,255,0.15);border-radius:50%;display:flex;align-items:center;justify-content:center;flex-shrink:0;\">\n            <svg width=\"22\" height=\"22\" viewBox=\"0 0 24 24\" fill=\"none\" stroke=\"white\" stroke-width=\"2\"><path d=\"M6 2l.01 6L10 12l-4.01 4.01L6 22h12l.01-6.01L14 12l4-4V2H6z\"/></svg>\n          </div>\n          <div>\n            <div style=\"font-size:21px;font-weight:700;color:white;letter-spacing:0.5px;line-height:1.1;\">Find my Perfect Size</div>\n            <div id=\"cuurtTimeLeft\" style=\"font-size:12px;color:rgba(255,255,255,0.75);margin-top:2px;\">Answer a few quick questions</div>\n          </div>\n        </div>\n        <button onclick=\"cuurtCloseQuiz()\" type=\"button\" style=\"background:rgba(255,255,255,0.15);border:none;cursor:pointer;color:white;border-radius:50%;width:32px;height:32px;display:flex;align-items:center;justify-content:center;flex-shrink:0;\">\n          <svg width=\"16\" height=\"16\" viewBox=\"0 0 24 24\" fill=\"none\" stroke=\"currentColor\" stroke-width=\"2.5\"><path d=\"M18 6L6 18M6 6l12 12\"/></svg>\n        </button>\n      </div>\n      <div id=\"cuurtStepDots\" style=\"display:flex;align-items:center;\"></div>\n    </div>\n\n    <div id=\"cuurtQuizBody\" style=\"flex:1;overflow-y:auto;\"></div>\n\n    <div id=\"cuurtQuizFooter\" style=\"padding:14px 24px;border-top:1px solid #e2e8f0;flex-shrink:0;display:none;\">\n      <!-- Filled per-step by cuurtGoTo(). Step 2 puts the \"measure my foot\"\n           button here so it is never buried below a scrolling brand list. -->\n      <div id=\"cuurtFooterExtra\"></div>\n      <button onclick=\"cuurtPrevStep()\" type=\"button\" style=\"background:none;border:none;cursor:pointer;font-size:13px;font-weight:600;color:#003C5F;display:flex;align-items:center;gap:6px;font-family:inherit;\">\n        <svg width=\"16\" height=\"16\" viewBox=\"0 0 24 24\" fill=\"none\" stroke=\"currentColor\" stroke-width=\"2.5\"><path d=\"M15 18l-6-6 6-6\"/></svg>\n        Previous Question\n      </button>\n    </div>\n  </div>\n</div>\n\n</div>";

  /* ╔═══════════════════════════════════════════════════════════════════════╗
     ║  TUNABLE CONSTANTS                                                    ║
     ║  Change these. Never change the logic below them.                     ║
     ╚═══════════════════════════════════════════════════════════════════════╝ */
  var TUNE = {

    /* ── THE THREE NUMBERS THAT DECIDE EVERYTHING ───────────────────────────

       Everything the engine does reduces to: estimate the foot, then pick the
       size whose clearance lands closest to TARGET, never below FLOOR, never
       above MAX. The eight hand-tuned thresholds of earlier drafts collapse
       into these three, which can be moved from real return data.

       clearance = CUURT internal length − customer's foot length            */

    /* What we AIM for: the middle of the 10-13 mm ideal range. */
    HOUSE_TARGET_MM: 11.5,

    /* Emergency minimum. Never violated. NOT what we aim for — the engine's
       own choice never lands below 8.2 mm (TARGET − half a size step),
       because at that point the next size up is closer to target. This floor
       only catches cases where the foot estimate was off. */
    MIN_CLEARANCE_MM: 7,

    /* Never exceeded. A court shoe with more than this in front slides on
       hard stops. At 17 mm the rule can't be honoured ~4% of the time; at 18
       that falls to ~1.5%. */
    MAX_CLEARANCE_MM: 18,

    /* One CUURT size step, mm. The ladder is a constant 6.6 mm per EU size. */
    SIZE_STEP_MM: 6.6,

    /* ── STEP 4: HOW MUCH ROOM IS IN FRONT OF THE TOES ──────────────────────

       This replaced "how do your shoes fit?" in v4. The old question anchored
       on the customer's SHOE; this one anchors on their FOOT, which is what
       the engine actually needs. Bands are contiguous on purpose — an earlier
       draft left 6-10 mm uncovered, so a customer with 8 mm had no honest
       answer and would pick one of the neighbours at random.               */
    TOE_GAP_BANDS: {
      1: { lo: 0,  hi: 5,  label: 'Almost touching', inch: 'less than 1/4 in' },
      2: { lo: 5,  hi: 10, label: 'A bit snug',      inch: 'about 1/4 in'     },
      3: { lo: 10, hi: 13, label: 'Just right',      inch: 'about 1/2 in'     },
      4: { lo: 13, hi: 17, label: 'A bit roomy',     inch: 'about 5/8 in'     },
      5: { lo: 17, hi: 24, label: 'Very roomy',      inch: '3/4 in or more'   }
    },

    /* The customer sits somewhere unknown inside the band they picked, so the
       assumed gap decides how the uncertainty is spent.
         low edge → 0% short but systematically over-sizes (median 13.9 mm)
         midpoint → centres well but 3.4% land under the floor
         midpoint − 1 → 0.9% under floor, median 12.5 mm  ← chosen
       Set to 0 to use the plain midpoint. */
    ASSUMED_GAP_SHIFT_MM: 1,

    /* Someone who deliberately sized up for width knows their own gap, so we
       trust the midpoint for them instead of leaning conservative. */
    TRUST_MIDPOINT_IF_SIZED_UP_FOR_WIDTH: true,

    /* Bands that trigger the "did you size up on purpose for width?" follow-up.
       Band 3 is deliberately absent: if they report the ideal gap there is
       nothing to explain away. */
    WIDTH_REASON_BANDS: [4, 5],

    /* ── TOE-ROOM PREFERENCE (separate question, separate job) ──────────────
       Step 4 measures how much room they HAVE. This is how much they WANT.
       Shifts the target only — never the floor. */
    TOE_PREFERENCE_DELTA_MM: { snug: -3, natural: 0, extra: 3 },

    /* ── WIDTH ──────────────────────────────────────────────────────────────
       CUURT is single-width, so length is the only lever and a poor one:
       one size up buys 1.58 mm of forefoot for 6.6 mm of length. The lever is
       MILLIMETRES, not sizes — every value below is under one step, so width
       can never move the recommendation by more than one size. That guarantee
       is structural, and asserted at load. */
    WIDTH_EXTRA_MM: {
      narrow:      0,
      normal:      0,
      wide:        2,
      'very-wide': 6.5
    },
    MAX_WIDTH_EXTRA_MM: 6.5,

    /* Float tolerance. The ladder is stored to one decimal, so 0.01 mm is far
       below anything physical and well above IEEE-754 noise. */
    EPSILON_MM: 0.01,

    /* ── DATA QUALITY ───────────────────────────────────────────────────────
       Wilson / Skechers / Babolat / K-Swiss / New Balance / Other step ~10 mm
       per full US size instead of the true 8.47 mm. They are accurate near
       EU 42 and drift both ways, so below EU 42 they UNDERSTATE the real
       insole. This recomputes the row on a true 6.667 mm/EU ladder anchored at
       that brand's own EU 42 and takes whichever value is LARGER — it can only
       move a recommendation up, never into short territory.
       Set false once competitor insoles are caliper-measured. */
    LADDER_CORRECTION_ENABLED: true,
    LADDER_TRUE_EU_STEP_MM: 20 / 3,
    LADDER_ANCHOR_EU: 42,
    LADDER_TRUSTED_BRANDS: ['Nike', 'Adidas', 'Asics'],

    /* ── COPY ───────────────────────────────────────────────────────────── */
    RUNS_LONG_NOTICE_MM: 15,     /* say so plainly above this much clearance */
    MAX_BENEFIT_LINES: 3
  };

  /* ╔═══════════════════════════════════════════════════════════════════════╗
     ║  UNIT HELPERS                                                         ║
     ╚═══════════════════════════════════════════════════════════════════════╝ */

  /* UK sizing for COMPETITOR brands only, barleycorn:
     UK = 3 × (foot length in inches) − 23, foot = insole less the ~12 mm
     allowance built into an athletic last. Checked against the one real UK
     column we hold (the official ASICS chart): 18 of 23 rows exact, 5 off by
     half a size. Where a brand row carries a real UK value we use that instead.

     CUURT's OWN UK labels are NOT computed here — they are transcribed from the
     published CUURT chart and live on each row of the CUURT table as `uk`. */
  function ukFromInsole(mm) {
    return Math.round((3 * ((mm - 12) / 25.4) - 23) * 2) / 2;
  }
  /* Brand EU rows are [label, euNumeric, insoleMm] and optionally a 4th real UK
     value (ASICS has one). Prefer the real number, fall back to the formula. */
  function ukLabelForRow(row) {
    return (row.length > 3 && row[3] != null) ? row[3] : ukFromInsole(row[2]);
  }

  /* ── Unit-aware length formatting ──────────────────────────────────────────
     US customers think in inches, so lead with inches and put mm in brackets.
     EU and UK customers think in mm, so show mm alone — an inch figure there is
     noise. Everything the customer reads about length goes through this. */
  function mmToIn(mm) { return mm / 25.4; }
  function fmtInch(mm) {
    var v = mmToIn(mm);
    /* Round to the nearest 1/16 in and reduce the fraction — "3/8 in" reads far
       better than "0.38 in" to someone holding a tape measure. */
    var whole = Math.floor(v);
    var sixteenths = Math.round((v - whole) * 16);
    if (sixteenths === 16) { whole += 1; sixteenths = 0; }
    if (sixteenths === 0) return whole + ' in';
    var num = sixteenths, den = 16;
    while (num % 2 === 0 && den % 2 === 0) { num /= 2; den /= 2; }
    return (whole > 0 ? whole + ' ' : '') + num + '/' + den + ' in';
  }
  /* THE formatter. unit is 'us' | 'eu' | 'uk' (defaults to the customer's
     selection, or mm when we have none). */
  function fmtLen(mm, unit) {
    var u = unit || (typeof ans !== 'undefined' && ans.unit) || 'eu';
    var mmTxt = (Math.round(mm * 10) / 10) + ' mm';
    return u === 'us' ? (fmtInch(mm) + ' (' + mmTxt + ')') : mmTxt;
  }
  /* Same, for a range. */
  function fmtRange(lo, hi, unit) {
    var u = unit || (typeof ans !== 'undefined' && ans.unit) || 'eu';
    /* A range starting at zero reads as "under X", not "0 in – X". */
    if (u === 'us') {
      return (lo === 0 ? 'under ' + fmtInch(hi) : fmtInch(lo) + '\u2013' + fmtInch(hi)) +
             ' (' + (lo === 0 ? 'under ' + hi : lo + '\u2013' + hi) + ' mm)';
    }
    return (lo === 0 ? 'under ' + hi : lo + '\u2013' + hi) + ' mm';
  }

  var ans  = blank();
  var step = 1;
  var TOTAL = 8;

  function blank() {
    return { brand: null, usSize: null, euSel: null, unit: 'us', gender: null, toeGap: null, sizedUpForWidth: null, width: null,
             toe: null, conditions: [], usage: null, footMm: null, path: 'brand' };
  }

  /* A dead click with no explanation is the worst failure mode a trigger can
     have — it looks like the widget is broken when the markup simply isn't on
     the page. Say so, precisely, once. */
  var warned = false;
  function missingMarkup(fn) {
    if (warned) return; warned = true;
    console.error('[CUURT] ' + fn + '() was called but the widget markup is not on this page.\n' +
      'The script normally installs it itself, so this usually means the script ran before\n' +
      '<body> existed. Load it with `defer`, or move the <script> tag to the end of <body>.');
  }

  /* ── PANEL ───────────────────────────────────────────────── */
  window.cuurtOpenPanel = function () {
    var o = document.getElementById('cuurtScOverlay');
    var p = document.getElementById('cuurtScPanel');
    var t = document.getElementById('cuurtTableMen');
    if (!o || !p || !t) { missingMarkup('cuurtOpenPanel'); return; }
    /* Tables are built lazily on first open — nothing to render until then. */
    if (!t.getAttribute('data-built')) {
      window.cuurtShowTable('men');
    }
    o.style.display = 'block';
    p.style.display = 'flex';
    setTimeout(function () { p.style.transform = 'translateX(0)'; }, 10);
    document.body.style.overflow = 'hidden';
  };
  window.cuurtClosePanel = function () {
    var p = document.getElementById('cuurtScPanel');
    if (p) p.style.transform = 'translateX(100%)';
    setTimeout(function () {
      var pp = document.getElementById('cuurtScPanel');
      var oo = document.getElementById('cuurtScOverlay');
      if (pp) pp.style.display = 'none';
      if (oo) oo.style.display = 'none';
    }, 350);
    document.body.style.overflow = '';
  };
  /* ── THE PUBLISHED SIZE CHART ────────────────────────────────────────────
     Built from CUURT[] at load rather than written into the markup by hand.
     A hand-copied chart drifts from the numbers the recommendation is actually
     computed with, and a customer who spots the difference is right to
     distrust both. This way they cannot diverge: EU is the ground truth, and
     usM / usW / uk are the published labels carried on the same rows.       */
  var TABLE_VIEWS = {
    Men:   { col: 'usM', head: 'US Men',   title: 'CUURT MUUV — Men',
             sub: 'Men’s US sizing — Wide Toe Box Court Shoe' },
    Women: { col: 'usW', head: 'US Women', title: 'CUURT MUUV — Women',
             sub: 'Women’s US sizing — Wide Toe Box Court Shoe' },
    Uk:    { col: 'uk',  head: 'UK',       title: 'CUURT MUUV — UK',
             sub: 'UK sizing — Wide Toe Box Court Shoe' }
  };
  var TH = 'background:#003C5F;color:white;font-size:11px;font-weight:600;letter-spacing:0.6px;text-transform:uppercase;padding:9px 8px;white-space:nowrap;text-align:';
  var TD = 'padding:8px;border-bottom:1px solid #e2e8f0;text-align:center;font-size:12.5px;white-space:nowrap;';
  var TD1 = 'padding:8px;border-bottom:1px solid #e2e8f0;font-weight:700;color:#003C5F;background:#f8fafc;font-size:12.5px;';

  function buildTable(key) {
    var v = TABLE_VIEWS[key];
    var h = '<div style="font-size:16px;font-weight:800;color:#003C5F;text-transform:uppercase;letter-spacing:1px;margin-bottom:3px;">' + v.title + '</div>';
    h += '<div style="font-size:12px;color:#6b7280;margin-bottom:12px;">' + v.sub + '</div>';
    h += '<div style="overflow-x:auto;-webkit-overflow-scrolling:touch;border-radius:12px;border:1px solid #e2e8f0;">';
    h += '<table style="width:100%;border-collapse:collapse;min-width:520px;"><thead><tr>';
    h += '<th style="' + TH + 'left;">EUR</th>';
    ['' + v.head, 'Length (in)', 'Length (mm)', 'Width (in)', 'Width (mm)'].forEach(function (c) {
      h += '<th style="' + TH + 'center;">' + c + '</th>';
    });
    h += '</tr></thead><tbody>';
    CUURT.forEach(function (r) {
      h += '<tr><td style="' + TD1 + '">' + r.eu + '</td>' +
           '<td style="' + TD + '">' + (r[v.col] || '—') + '</td>' +
           '<td style="' + TD + '">' + (r.len / 25.4).toFixed(2) + '</td>' +
           '<td style="' + TD + '">' + r.len.toFixed(1) + '</td>' +
           '<td style="' + TD + '">' + (r.wid / 25.4).toFixed(2) + '</td>' +
           '<td style="' + TD + '">' + r.wid.toFixed(2) + '</td></tr>';
    });
    return h + '</tbody></table></div>';
  }

  window.cuurtShowTable = function (which) {
    ['Men', 'Women', 'Uk'].forEach(function (k) {
      var pane = document.getElementById('cuurtTable' + k);
      var tab  = document.getElementById('cuurtTab' + k);
      var on   = (k.toLowerCase() === which);
      if (pane) {
        if (!pane.getAttribute('data-built')) {
          pane.innerHTML = buildTable(k);
          pane.setAttribute('data-built', '1');
        }
        pane.style.display = on ? 'block' : 'none';
      }
      if (tab)  { tab.style.background = on ? '#003C5F' : '#f4f6f8';
                  tab.style.color      = on ? 'white'   : '#6b7280'; }
    });
  };

  /* ── QUIZ SHELL ──────────────────────────────────────────── */
  window.cuurtOpenQuiz = function () {
    cuurtClosePanel();
    ans = blank();
    step = 1;
    var o = document.getElementById('cuurtQuizOverlay');
    if (!o) { missingMarkup('cuurtOpenQuiz'); return; }
    o.style.display = 'flex';
    document.body.style.overflow = 'hidden';
    track('cuurt_quiz_start', {});
    cuurtGoTo(1);
  };
  window.cuurtCloseQuiz = function () {
    var o = document.getElementById('cuurtQuizOverlay');
    if (o) o.style.display = 'none';
    document.body.style.overflow = '';
  };
  window.cuurtPrevStep = function () {
    if (step === 'measure')  { cuurtGoTo(2); return; }
    if (step === 'whyroomy') { cuurtGoTo(4); return; }
    if (step === 5 && ans.path === 'measure') { cuurtGoTo('measure'); return; }
    if (typeof step === 'number' && step > 1) cuurtGoTo(step - 1);
  };

  window.cuurtGoTo = function (s) {
    step = s;
    var tl = document.getElementById('cuurtTimeLeft');
    if (tl) tl.textContent = 'A few quick questions \u2022 about 60 seconds';
    var footer = document.getElementById('cuurtQuizFooter');
    if (footer) {
      footer.style.display = (s === 1) ? 'none' : '';   /* result fills it via renderResult() */
      /* Step 2's "measure my foot instead" used to sit at the bottom of a long
         scrolling list of brands, where it was invisible on a phone. It now
         lives in the fixed footer as a full-width button. */
      var prevBtn = footer.querySelector('button');
      if (prevBtn) prevBtn.style.display = (s === 'result') ? 'none' : '';
      if (s !== 'result') { footer.style.padding = '14px 24px'; }
      var slot = document.getElementById('cuurtFooterExtra');
      if (slot && s !== 'result') {
        slot.innerHTML = (s === 2)
          ? '<div onclick="cuurtGoTo(\'measure\')" style="display:flex;align-items:center;justify-content:center;gap:10px;padding:13px 16px;border:1.5px solid #003C5F;border-radius:12px;cursor:pointer;background:#eaf4fb;margin-bottom:10px;" onmouseover="this.style.background=\'#d9edf9\'" onmouseout="this.style.background=\'#eaf4fb\'">' +
              '<span style="font-size:19px;">📏</span>' +
              '<div style="text-align:left;"><div style="font-weight:700;font-size:14px;color:#003C5F;line-height:1.2;">Measure my foot instead</div>' +
              '<div style="font-size:11.5px;color:#6b7280;margin-top:1px;">2 minutes — the most accurate option</div></div></div>'
          : '';
      }
    }
    renderDots(s);
    var body = document.getElementById('cuurtQuizBody');
    if (!body) return;
    /* Hard guard: gender drives the headline number, so nothing downstream
       may render without it. Bounces back to step 1 rather than guessing. */
    if (s !== 1 && !ans.gender) { cuurtGoTo(1); return; }
    if (s === 'result') { renderResult(); return; }
    if (s === 'measure')  { body.innerHTML = sMeasure(); return; }
    if (s === 'whyroomy') { body.innerHTML = sWhyRoomy(); return; }
    var screens = [null, sGender, s1, s2, s3, s4, s5, s6, s7];
    if (screens[s]) body.innerHTML = screens[s]();
  };

  function renderDots(s) {
    var el = document.getElementById('cuurtStepDots');
    if (!el) return;
    var n = s === 'result' ? 9 : s === 'measure' ? 3 : s === 'whyroomy' ? 4 : parseInt(s, 10);
    var h = '';
    for (var i = 1; i <= TOTAL; i++) {
      var active = i === n, done = i < n;
      h += '<div style="width:30px;height:30px;border-radius:50%;background:' + ((done || active) ? 'white' : 'rgba(255,255,255,0.25)') +
        ';color:' + ((done || active) ? '#003C5F' : 'rgba(255,255,255,0.6)') +
        ';font-size:12px;font-weight:600;display:flex;align-items:center;justify-content:center;flex-shrink:0;box-shadow:' +
        (active ? '0 0 0 3px rgba(255,255,255,0.3)' : 'none') + ';">' + i + '</div>';
      if (i < TOTAL) h += '<div style="flex:1;height:2px;background:' + (done ? 'white' : 'rgba(255,255,255,0.25)') + ';"></div>';
    }
    el.innerHTML = h;
  }

  /* ── SCREENS ─────────────────────────────────────────────── */
  /* ── STEP 1: WHO ARE WE SIZING FOR? ────────────────────────────────────
     This used to be a small MEN'S / WOMEN'S toggle buried on the size screen,
     defaulted to men's and easy to miss. That was tolerable when EU was the
     headline (CUURT is unisex, so gender only ever changed a label). It is NOT
     tolerable now that the US size is the headline: a missed toggle shows a
     woman a men's number 1.5 sizes off what she'd recognise. So it is an
     explicit, un-skippable first question with no default. ─────────────── */
  function sGender() {
    var h = '<div style="padding:28px 24px;">';
    h += '<div style="font-size:21px;font-weight:700;color:#1a1a2e;text-align:center;margin-bottom:6px;">Who are we sizing for?</div>';
    h += '<div style="font-size:13px;color:#6b7280;text-align:center;margin-bottom:24px;">CUURT is unisex — this only sets which US size we show you</div>';
    h += '<div style="display:grid;grid-template-columns:1fr 1fr;gap:12px;">';
    [['men','Men’s','US men’s sizing'],['women','Women’s','US women’s sizing']].forEach(function (o) {
      var sel = ans.gender === o[0];
      h += '<div onclick="cuurtSelPersonGender(\'' + o[0] + '\')" style="padding:24px 16px;border:1.5px solid ' + (sel ? '#003C5F' : '#e2e8f0') + ';border-radius:12px;cursor:pointer;background:' + (sel ? '#eaf4fb' : 'white') + ';text-align:center;" onmouseover="this.style.borderColor=\'#003C5F\';this.style.background=\'#eaf4fb\'" onmouseout="this.style.borderColor=\'' + (sel ? '#003C5F' : '#e2e8f0') + '\';this.style.background=\'' + (sel ? '#eaf4fb' : 'white') + '\'">';
      h += '<div style="font-weight:700;font-size:17px;color:#1a1a2e;margin-bottom:4px;">' + o[1] + '</div>';
      h += '<div style="font-size:12px;color:#6b7280;line-height:1.3;">' + o[2] + '</div></div>';
    });
    h += '</div>';
    h += '<div style="margin-top:18px;padding:12px 16px;background:rgba(0,168,232,0.12);border-radius:8px;font-size:12px;color:#003C5F;line-height:1.5;">Same shoe either way — men and women share one last and one EU ladder. We just want to show you the number you actually shop in.</div>';
    h += '</div>';
    return h;
  }

  window.cuurtSelPersonGender = function (g) {
    ans.gender = g;
    setTimeout(function () { cuurtGoTo(2); }, 160);
  };

  function s1() {
    var pop  = ['Nike', 'Adidas', 'Wilson'];
    var rest = ['Skechers', 'Babolat', 'Asics', 'K-Swiss', 'New Balance', 'Other'];
    var emap = { Skechers: '\uD83D\uDC5F', Babolat: '\uD83C\uDFC6', Asics: '\u26A1', 'K-Swiss': '\uD83C\uDFBE', 'New Balance': '\uD83C\uDFC3', Other: '+' };
    var h = '<div style="padding:28px 24px;">';
    h += '<div style="font-size:21px;font-weight:700;color:#1a1a2e;text-align:center;margin-bottom:6px;">What brand do you usually wear?</div>';
    h += '<div style="font-size:13px;color:#6b7280;text-align:center;margin-bottom:24px;">We match its real internal measurements to CUURT\u2019s</div>';
    h += '<div style="font-size:11px;font-weight:700;letter-spacing:1.5px;text-transform:uppercase;color:#6b7280;margin-bottom:10px;">MOST POPULAR</div>';
    h += '<div style="display:flex;flex-direction:column;gap:8px;margin-bottom:18px;">';
    pop.forEach(function (b) {
      h += '<div onclick="cuurtSelBrand(\'' + b + '\')" style="display:flex;align-items:center;justify-content:space-between;padding:14px 18px;border:1.5px solid #e2e8f0;border-radius:12px;cursor:pointer;font-weight:600;font-size:15px;color:#1a1a2e;background:white;" onmouseover="this.style.borderColor=\'#003C5F\';this.style.background=\'#eaf4fb\'" onmouseout="this.style.borderColor=\'#e2e8f0\';this.style.background=\'white\'"><span>' + b + '</span><span style="opacity:0.5;">\u2794</span></div>';
    });
    h += '</div>';
    h += '<div style="font-size:11px;font-weight:700;letter-spacing:1.5px;text-transform:uppercase;color:#6b7280;margin-bottom:10px;">OTHER BRANDS</div>';
    h += '<div style="display:grid;grid-template-columns:1fr 1fr;gap:10px;margin-bottom:18px;">';
    rest.forEach(function (b) {
      h += '<div onclick="cuurtSelBrand(\'' + b + '\')" style="display:flex;flex-direction:column;align-items:center;gap:6px;padding:16px 12px;border:1.5px solid #e2e8f0;border-radius:12px;cursor:pointer;font-size:13px;font-weight:500;color:#1a1a2e;background:white;text-align:center;" onmouseover="this.style.borderColor=\'#003C5F\';this.style.background=\'#eaf4fb\'" onmouseout="this.style.borderColor=\'#e2e8f0\';this.style.background=\'white\'"><span style="font-size:22px;">' + emap[b] + '</span>' + b + '</div>';
    });
    h += '</div>';
    h += '</div>';
    return h;
  }

  function sMeasure() {
    ans.path = 'measure';
    var h = '<div style="padding:28px 24px;">';
    h += '<div style="font-size:21px;font-weight:700;color:#1a1a2e;text-align:center;margin-bottom:6px;">Measure your foot length</div>';
    h += '<div style="font-size:13px;color:#6b7280;text-align:center;margin-bottom:20px;">Direct measurement beats any size conversion</div>';
    h += '<div style="background:#f8fafc;border:1px solid #e2e8f0;border-radius:12px;padding:16px 18px;margin-bottom:20px;font-size:13px;color:#1a1a2e;line-height:1.6;">';
    h += '<strong>How to measure:</strong><br>1. Stand on a sheet of paper against a wall, heel touching the wall.<br>2. Mark the tip of your longest toe (it may not be the big toe).<br>3. Measure wall to mark in cm. Do both feet \u2014 use the longer one.<br><span style="color:#6b7280;">Tip: measure in the evening, wearing your court socks.</span></div>';
    h += '<div style="display:flex;border:1px solid #e2e8f0;border-radius:8px;overflow:hidden;margin-bottom:16px;">';
    h += '<button id="cuurtMgM" onclick="cuurtMeasureGender(\'men\')" type="button" style="flex:1;padding:8px;background:' + (ans.gender === 'men' ? '#003C5F' : '#f4f6f8') + ';border:none;cursor:pointer;font-size:12px;font-weight:600;color:' + (ans.gender === 'men' ? 'white' : '#6b7280') + ';text-transform:uppercase;letter-spacing:0.8px;font-family:inherit;">MEN\u2019S</button>';
    h += '<button id="cuurtMgW" onclick="cuurtMeasureGender(\'women\')" type="button" style="flex:1;padding:8px;background:' + (ans.gender === 'women' ? '#003C5F' : '#f4f6f8') + ';border:none;cursor:pointer;font-size:12px;font-weight:600;color:' + (ans.gender === 'women' ? 'white' : '#6b7280') + ';text-transform:uppercase;letter-spacing:0.8px;font-family:inherit;">WOMEN\u2019S</button>';
    h += '</div>';
    var inMode = ans.unit === 'us';
    h += '<label style="display:block;font-size:12px;font-weight:700;letter-spacing:1px;text-transform:uppercase;color:#6b7280;margin-bottom:8px;">Foot length (' + (inMode ? 'inches' : 'cm') + ')</label>';
    h += '<div style="display:flex;gap:10px;">';
    h += '<input id="cuurtFootCm" type="number" inputmode="decimal" min="' + (inMode ? '7' : '18') + '" max="' + (inMode ? '14' : '35') + '" step="0.1" placeholder="' + (inMode ? 'e.g. 10.4' : 'e.g. 26.4') + '" style="flex:1;padding:14px 16px;border:1.5px solid #e2e8f0;border-radius:12px;font-size:16px;font-family:inherit;color:#1a1a2e;" />';
    h += '<button onclick="cuurtSubmitFoot()" type="button" style="padding:14px 22px;background:#003C5F;color:white;border:none;border-radius:12px;font-size:15px;font-weight:700;cursor:pointer;font-family:inherit;">Continue \u2794</button>';
    h += '</div>';
    h += '<div id="cuurtFootErr" style="display:none;font-size:12px;color:#991b1b;margin-top:8px;"></div>';
    h += '</div>';
    return h;
  }

  window.cuurtMeasureGender = function (g) {
    ans.gender = g;
    var m = document.getElementById('cuurtMgM'), w = document.getElementById('cuurtMgW');
    if (m) { m.style.background = g === 'men' ? '#003C5F' : '#f4f6f8'; m.style.color = g === 'men' ? 'white' : '#6b7280'; }
    if (w) { w.style.background = g === 'women' ? '#003C5F' : '#f4f6f8'; w.style.color = g === 'women' ? 'white' : '#6b7280'; }
  };

  window.cuurtSubmitFoot = function () {
    var el = document.getElementById('cuurtFootCm');
    var err = document.getElementById('cuurtFootErr');
    var v = parseFloat(el && el.value);
    /* US customers are typing inches, everyone else centimetres. */
    var inMode = ans.unit === 'us';
    var lo = inMode ? 7 : 18, hi = inMode ? 14 : 35;
    if (!v || v < lo || v > hi) {
      if (err) { err.style.display = 'block';
        err.textContent = 'Enter a value between ' + lo + ' and ' + hi + (inMode ? ' inches.' : ' cm.'); }
      return;
    }
    ans.footMm = Math.round((inMode ? v * 25.4 : v * 10) * 10) / 10;
    ans.brand = null; ans.usSize = null; ans.toeGap = null; ans.sizedUpForWidth = null;
    cuurtGoTo(5);
  };

  function s2() {
    var h = '<div style="padding:28px 24px;">';
    h += '<div style="font-size:21px;font-weight:700;color:#1a1a2e;text-align:center;margin-bottom:6px;">What\u2019s your size in ' + ans.brand + '?</div>';
    h += '<div style="font-size:13px;color:#6b7280;text-align:center;margin-bottom:20px;">Use the size printed on your ' + ans.brand + ' box or label</div>';
    h += '<div style="display:flex;border:1px solid #e2e8f0;border-radius:8px;overflow:hidden;margin-bottom:10px;">';
    ['us','eu','uk'].forEach(function (u) {
      var on = ans.unit === u;
      h += '<button onclick="cuurtSelUnit(\'' + u + '\')" type="button" style="flex:1;padding:10px;background:' + (on ? '#003C5F' : '#f4f6f8') + ';border:none;cursor:pointer;font-size:13px;font-weight:600;color:' + (on ? 'white' : '#6b7280') + ';text-transform:uppercase;letter-spacing:0.8px;font-family:inherit;">' + u.toUpperCase() + ' SIZES</button>';
    });
    h += '</div>';
    h += '<div style="display:flex;border:1px solid #e2e8f0;border-radius:8px;overflow:hidden;margin-bottom:20px;">';
    h += '<button onclick="cuurtSelGender(\'men\')" type="button" style="flex:1;padding:8px;background:' + (ans.gender === 'men' ? '#003C5F' : '#f4f6f8') + ';border:none;cursor:pointer;font-size:12px;font-weight:600;color:' + (ans.gender === 'men' ? 'white' : '#6b7280') + ';text-transform:uppercase;letter-spacing:0.8px;font-family:inherit;">MEN\u2019S</button>';
    h += '<button onclick="cuurtSelGender(\'women\')" type="button" style="flex:1;padding:8px;background:' + (ans.gender === 'women' ? '#003C5F' : '#f4f6f8') + ';border:none;cursor:pointer;font-size:12px;font-weight:600;color:' + (ans.gender === 'women' ? 'white' : '#6b7280') + ';text-transform:uppercase;letter-spacing:0.8px;font-family:inherit;">WOMEN\u2019S</button>';
    h += '</div>';
    if (ans.unit === 'eu' || ans.unit === 'uk') {
      var list = euList(ans.brand, ans.gender);
      h += '<div style="display:grid;grid-template-columns:repeat(5,1fr);gap:8px;">';
      list.forEach(function (row, i) {
        var lab = ans.unit === 'uk' ? ukLabelForRow(row) : row[0];
        h += '<div onclick="cuurtSelSizeEu(' + i + ')" style="aspect-ratio:1;border:1.5px solid #e2e8f0;border-radius:8px;background:white;cursor:pointer;font-size:13px;font-weight:600;color:#1a1a2e;display:flex;align-items:center;justify-content:center;min-height:48px;" onmouseover="this.style.borderColor=\'#003C5F\'" onmouseout="this.style.borderColor=\'#e2e8f0\'">' + lab + '</div>';
      });
      h += '</div>';
      var t = BDEU[ans.brand] || BDEU['Other'];
      if (t.u && ['Wilson','Adidas','Nike','New Balance','Asics'].indexOf(ans.brand) > -1) {
        h += '<div style="font-size:11px;color:#6b7280;margin-top:10px;text-align:center;">' + ans.brand + ' ' + ans.unit.toUpperCase() + ' sizes are the same for men and women</div>';
      }
      if (ans.unit === 'uk' && ans.brand !== 'Asics') {
        h += '<div style="font-size:11px;color:#6b7280;margin-top:6px;text-align:center;">UK sizes converted from ' + ans.brand + '\u2019s measurements</div>';
      }
    } else {
      var data = BD[ans.brand] || BD['Other'];
      var tbl = (data[ans.gender] && Object.keys(data[ans.gender]).length) ? data[ans.gender] : data['men'];
      var sizes = Object.keys(tbl).map(Number).sort(function (a, b) { return a - b; });
      h += '<div style="display:grid;grid-template-columns:repeat(5,1fr);gap:8px;">';
      sizes.forEach(function (sz) {
        h += '<div onclick="cuurtSelSize(' + sz + ')" style="aspect-ratio:1;border:1.5px solid #e2e8f0;border-radius:8px;background:white;cursor:pointer;font-size:13px;font-weight:600;color:#1a1a2e;display:flex;align-items:center;justify-content:center;min-height:48px;" onmouseover="this.style.borderColor=\'#003C5F\'" onmouseout="this.style.borderColor=\'#e2e8f0\'">' + sz + '</div>';
      });
      h += '</div>';
    }
    h += '</div>';
    return h;
  }

  /* ── STEP 4: HOW MUCH ROOM IS IN FRONT OF THE TOES ─────────────────────
     Replaces "how do your shoes fit?". That question anchored on the SHOE and
     told us nothing about the foot inside it, so anyone whose current shoe had
     spare room got sized up as if they needed all of it. This one measures the
     gap, which is what lets us work backwards to the foot. ───────────────── */
  function s3() {
    var h = '<div style="padding:28px 24px;">';
    h += '<div style="font-size:21px;font-weight:700;color:#1a1a2e;text-align:center;margin-bottom:6px;">How much space is in front of your toes?</div>';
    h += '<div style="font-size:13px;color:#6b7280;text-align:center;margin-bottom:16px;">In your ' + ans.brand + ', standing up. This is the single most useful thing you can tell us.</div>';

    h += '<div style="background:#f8fafc;border:1px solid #e2e8f0;border-radius:12px;padding:14px 16px;margin-bottom:18px;font-size:12.5px;color:#1a1a2e;line-height:1.6;">';
    h += '<strong>Not sure?</strong> Pull the insole out, put it on the floor and stand on it. Look at how much insole shows in front of your longest toe — that is the number. It is what a fitter does.';
    h += '</div>';

    h += '<div style="display:flex;flex-direction:column;gap:10px;">';
    [1,2,3,4,5].forEach(function (k) {
      var b = TUNE.TOE_GAP_BANDS[k];
      var range = k === 5
        ? (ans.unit === 'us' ? fmtInch(b.lo) + ' +' : b.lo + ' mm +')
        : fmtRange(b.lo, b.hi);
      var ideal = k === 3;
      h += '<div onclick="cuurtSelToeGap(' + k + ')" style="display:flex;align-items:center;justify-content:space-between;gap:12px;padding:14px 18px;border:1.5px solid ' + (ideal ? '#2d7a3e' : '#e2e8f0') + ';border-radius:12px;cursor:pointer;background:' + (ideal ? '#f0f7ee' : 'white') + ';" onmouseover="this.style.borderColor=\'#003C5F\';this.style.background=\'#eaf4fb\'" onmouseout="this.style.borderColor=\'' + (ideal ? '#2d7a3e' : '#e2e8f0') + '\';this.style.background=\'' + (ideal ? '#f0f7ee' : 'white') + '\'">';
      h += '<div><div style="font-weight:600;font-size:15px;color:#1a1a2e;">' + b.label + (ideal ? ' <span style="font-size:11px;font-weight:700;color:#2d7a3e;letter-spacing:0.5px;">· IDEAL</span>' : '') + '</div>';
      /* US leads with the inch phrasing; EU/UK get the plain-language cue
         instead, since an inch figure means nothing to them. */
      /* The range chip already carries the precise figure, so the cue below the
         label stays plain-language for every unit. Showing b.inch here too gave
         US customers two inch numbers that disagreed by a sixteenth. */
      var cue = (k === 3 ? 'the sweet spot' : k < 3 ? 'less than ideal' : 'more than needed');
      h += '<div style="font-size:12px;color:#6b7280;margin-top:2px;">' + cue + '</div></div>';
      h += '<div style="font-size:12px;font-weight:600;color:#003C5F;white-space:nowrap;">' + range + '</div></div>';
    });
    h += '</div></div>';
    return h;
  }

  /* Follow-up, bands 4 and 5 only. A large gap has two very different causes:
     the shoe is simply too big, or they sized up deliberately to get width.
     The second is common among wide-footed customers and is the one signal
     that justifies sizing them down with confidence. Band 3 does not ask —
     if the gap is already ideal there is nothing to explain. */
  function sWhyRoomy() {
    var b = TUNE.TOE_GAP_BANDS[ans.toeGap];
    var h = '<div style="padding:28px 24px;">';
    h += '<div style="font-size:21px;font-weight:700;color:#1a1a2e;text-align:center;margin-bottom:6px;">Did you buy that size on purpose?</div>';
    h += '<div style="font-size:13px;color:#6b7280;text-align:center;margin-bottom:24px;">You said ' + b.label.toLowerCase() + ' — plenty of people size up to get a wider shoe and accept the extra length</div>';
    h += '<div style="display:flex;flex-direction:column;gap:10px;">';
    [['yes','Yes — I sized up to get more width','I needed the width and took the length that came with it'],
     ['no','No — that is just how they fit','I bought my usual size and it came out roomy']
    ].forEach(function (o) {
      h += '<div onclick="cuurtSelWhyRoomy(\'' + o[0] + '\')" style="padding:16px 18px;border:1.5px solid #e2e8f0;border-radius:12px;cursor:pointer;background:white;" onmouseover="this.style.borderColor=\'#003C5F\';this.style.background=\'#eaf4fb\'" onmouseout="this.style.borderColor=\'#e2e8f0\';this.style.background=\'white\'">';
      h += '<div style="font-weight:600;font-size:15px;color:#1a1a2e;">' + o[1] + '</div>';
      h += '<div style="font-size:12px;color:#6b7280;margin-top:3px;line-height:1.4;">' + o[2] + '</div></div>';
    });
    h += '</div>';
    h += '<div style="margin-top:16px;padding:12px 16px;background:rgba(0,168,232,0.12);border-radius:8px;font-size:12px;color:#003C5F;line-height:1.5;">If you sized up for width, our toe box may let you come back to your true length. That is the whole point of it.</div>';
    h += '</div>';
    return h;
  }

  function s4() {
    var h = '<div style="padding:28px 24px;">';
    h += '<div style="font-size:21px;font-weight:700;color:#1a1a2e;text-align:center;margin-bottom:6px;">How would you describe your foot width?</div>';
    h += '<div style="font-size:13px;color:#6b7280;text-align:center;margin-bottom:16px;">Width drives most sizing mistakes \u2014 be honest here</div>';
    h += '<div style="display:inline-flex;align-items:center;gap:6px;background:rgba(0,168,232,0.12);color:#003C5F;font-size:12px;font-weight:500;padding:7px 12px;border-radius:20px;margin-bottom:18px;">\u2728 CUURT\u2019s toe box is wider than every brand in this quiz</div>';
    var opts = [
      { v: 'narrow',    l: 'Narrow',    d: 'Slim feet, shoes often feel roomy' },
      { v: 'normal',    l: 'Normal',    d: 'Standard width, most shoes fit' },
      { v: 'wide',      l: 'Wide',      d: 'Need extra room in the toe box' },
      { v: 'very-wide', l: 'Very wide', d: 'Often size up just for width' }
    ];
    h += '<div style="display:grid;grid-template-columns:1fr 1fr;gap:10px;">';
    opts.forEach(function (o) {
      h += '<div onclick="cuurtSelWidth(\'' + o.v + '\')" style="padding:16px;border:1.5px solid #e2e8f0;border-radius:12px;cursor:pointer;background:white;" onmouseover="this.style.borderColor=\'#003C5F\';this.style.background=\'#eaf4fb\'" onmouseout="this.style.borderColor=\'#e2e8f0\';this.style.background=\'white\'">';
      h += '<div style="font-weight:600;font-size:14px;color:#1a1a2e;margin-bottom:4px;">' + o.l + '</div>';
      h += '<div style="font-size:12px;color:#6b7280;line-height:1.3;">' + o.d + '</div></div>';
    });
    h += '</div></div>';
    return h;
  }

  function s5() {
    return optList(
      'How much toe room do you prefer?',
      'This sets the space we reserve in front of your toes',
      [
        { v: 'snug',    e: '\uD83E\uDD0F', l: 'Snug fit',    d: 'Performance feel' },
        { v: 'natural', e: '\uD83D\uDE0A', l: 'Natural fit', d: 'Comfortable wiggle room' },
        { v: 'extra',   e: '\uD83E\uDDB6', l: 'Extra space', d: 'Maximum toe freedom' }
      ],
      'cuurtSelToe'
    );
  }

  function s6() {
    var conds = [
      { v: 'bunions',    l: 'Bunions',            d: 'Bony bump at the big toe joint' },
      { v: 'hammer',     l: 'Hammer / claw toes', d: 'Toes that bend or overlap' },
      { v: 'neuropathy', l: 'Neuropathy',         d: 'Numbness or tingling' },
      { v: 'flat',       l: 'Flat feet',          d: 'Low or no arch' },
      { v: 'plantar',    l: 'Plantar fasciitis',  d: 'Heel pain' },
      { v: 'none',       l: 'None',               d: 'No specific conditions' }
    ];
    var h = '<div style="padding:28px 24px;">';
    h += '<div style="font-size:21px;font-weight:700;color:#1a1a2e;text-align:center;margin-bottom:6px;">Do you experience any of these?</div>';
    h += '<div style="font-size:13px;color:#6b7280;text-align:center;margin-bottom:24px;">Select all that apply \u2014 some need extra forefoot room</div>';
    h += '<div style="display:grid;grid-template-columns:1fr 1fr;gap:10px;">';
    conds.forEach(function (c) {
      h += '<div id="cc_' + c.v + '" onclick="cuurtToggleCond(\'' + c.v + '\')" style="padding:16px;border:1.5px solid #e2e8f0;border-radius:12px;cursor:pointer;background:white;" onmouseover="this.style.borderColor=\'#003C5F\'" onmouseout="if(!this.dataset.sel)this.style.borderColor=\'#e2e8f0\'">';
      h += '<div style="font-weight:600;font-size:14px;color:#1a1a2e;margin-bottom:4px;">' + c.l + '</div>';
      h += '<div style="font-size:12px;color:#6b7280;line-height:1.3;">' + c.d + '</div></div>';
    });
    h += '</div>';
    h += '<button onclick="cuurtGoTo(8)" type="button" style="width:100%;padding:15px;background:#003C5F;color:white;border:none;border-radius:12px;font-size:15px;font-weight:700;cursor:pointer;margin-top:20px;font-family:inherit;">Continue \u2794</button>';
    h += '</div>';
    return h;
  }

  function s7() {
    var items = [
      { v: 'pickleball', e: '\uD83E\uDD52', l: 'Pickleball',      d: 'Recreational & competitive' },
      { v: 'tennis',     e: '\uD83C\uDFBE', l: 'Tennis',          d: 'Court tennis, all surfaces' },
      { v: 'padel',      e: '\uD83C\uDFD3', l: 'Padel / Squash',  d: 'Indoor court sports' },
      { v: 'training',   e: '\uD83D\uDCAA', l: 'Training',        d: 'Drills & intensive practice' },
      { v: 'multi',      e: '\uD83C\uDFC5', l: 'Multi-sport',     d: 'Mix of court sports & activities' }
    ];
    var h = '<div style="padding:28px 24px;">';
    h += '<div style="font-size:21px;font-weight:700;color:#1a1a2e;text-align:center;margin-bottom:6px;">Where will you mostly wear these?</div>';
    h += '<div style="font-size:13px;color:#6b7280;text-align:center;margin-bottom:24px;">Last question</div>';
    h += '<div style="display:grid;grid-template-columns:1fr 1fr;gap:10px;margin-bottom:14px;">';
    items.forEach(function (o) {
      var span = o.v === 'multi' ? 'grid-column:span 2;' : '';
      h += '<div id="cu_' + o.v + '" onclick="cuurtSelUsage(\'' + o.v + '\')" style="' + span + 'padding:20px 14px;border:1.5px solid #e2e8f0;border-radius:12px;cursor:pointer;background:white;text-align:center;" onmouseover="this.style.borderColor=\'#003C5F\';this.style.background=\'#eaf4fb\'" onmouseout="this.style.borderColor=\'#e2e8f0\';this.style.background=\'white\'">';
      h += '<div style="font-size:28px;margin-bottom:8px;">' + o.e + '</div>';
      h += '<div style="font-weight:700;font-size:13px;color:#1a1a2e;margin-bottom:3px;">' + o.l + '</div>';
      h += '<div style="font-size:11px;color:#6b7280;line-height:1.3;">' + o.d + '</div></div>';
    });
    h += '</div>';
    h += '<div id="cuurtUsageWrap" style="border:1.5px solid #e2e8f0;border-radius:12px;padding:14px 16px;background:white;">';
    h += '<div style="font-size:12px;font-weight:700;letter-spacing:1px;text-transform:uppercase;color:#6b7280;margin-bottom:8px;">Something else?</div>';
    h += '<input id="cuurtUsageInput" type="text" maxlength="40" placeholder="e.g. badminton, walking, CrossFit\u2026" oninput="cuurtUsageTyped(this.value)" style="width:100%;padding:12px 14px;border:1.5px solid #e2e8f0;border-radius:8px;font-size:14px;font-family:inherit;color:#1a1a2e;box-sizing:border-box;" />';
    h += '<button id="cuurtUsageSubmit" onclick="cuurtSubmitUsage()" type="button" style="display:none;margin-top:10px;width:100%;padding:12px;background:#003C5F;color:white;border:none;border-radius:8px;font-size:14px;font-weight:600;cursor:pointer;font-family:inherit;">Continue \u2794</button>';
    h += '</div>';
    h += '</div>';
    return h;
  }

  window.cuurtUsageTyped = function (val) {
    var btn = document.getElementById('cuurtUsageSubmit');
    var wrap = document.getElementById('cuurtUsageWrap');
    if (!btn || !wrap) return;
    if (val.trim().length > 0) {
      btn.style.display = 'block';
      wrap.style.borderColor = '#003C5F'; wrap.style.background = '#eaf4fb';
    } else {
      btn.style.display = 'none';
      wrap.style.borderColor = '#e2e8f0'; wrap.style.background = 'white';
    }
  };
  window.cuurtSubmitUsage = function () {
    var el = document.getElementById('cuurtUsageInput');
    var v = (el && el.value || '').trim();
    if (!v) return;
    ans.usage = 'custom:' + v;
    setTimeout(function () { cuurtGoTo('result'); }, 150);
  };

  function optList(title, sub, opts, fn) {
    var h = '<div style="padding:28px 24px;">';
    h += '<div style="font-size:21px;font-weight:700;color:#1a1a2e;text-align:center;margin-bottom:6px;">' + title + '</div>';
    h += '<div style="font-size:13px;color:#6b7280;text-align:center;margin-bottom:24px;">' + sub + '</div>';
    h += '<div style="display:flex;flex-direction:column;gap:10px;">';
    opts.forEach(function (o) {
      h += '<div onclick="' + fn + '(\'' + o.v + '\')" style="display:flex;align-items:center;gap:14px;padding:14px 18px;border:1.5px solid #e2e8f0;border-radius:12px;cursor:pointer;background:white;" onmouseover="this.style.borderColor=\'#003C5F\';this.style.background=\'#eaf4fb\'" onmouseout="this.style.borderColor=\'#e2e8f0\';this.style.background=\'white\'">';
      if (o.e) h += '<span style="font-size:22px;flex-shrink:0;width:32px;text-align:center;">' + o.e + '</span>';
      h += '<div><div style="font-weight:600;font-size:15px;color:#1a1a2e;">' + o.l + '</div><div style="font-size:12px;color:#6b7280;margin-top:2px;">' + o.d + '</div></div></div>';
    });
    h += '</div></div>';
    return h;
  }

  /* ── HANDLERS ────────────────────────────────────────────── */
  window.cuurtSelBrand  = function (b) { ans.brand = b; ans.path = 'brand'; ans.footMm = null; ans.usSize = null; ans.euSel = null; cuurtGoTo(3); };
  window.cuurtSelGender = function (g) { ans.gender = g; ans.usSize = null; ans.euSel = null; cuurtGoTo(3); };  /* size-screen toggle — keeps step 1 and this screen in sync */
  window.cuurtSelUnit   = function (u) { ans.unit = u; ans.usSize = null; ans.euSel = null; cuurtGoTo(3); };
  window.cuurtSelSizeEu = function (i) {
    var row = euList(ans.brand, ans.gender)[i];
    if (!row) return;
    ans.euSel = { label: row[0], num: row[1], mm: row[2], uk: ukLabelForRow(row) };
    ans.usSize = null;
    setTimeout(function () { cuurtGoTo(4); }, 180);
  };
  window.cuurtSelSize   = function (sz) { ans.usSize = sz; setTimeout(function () { cuurtGoTo(4); }, 180); };
  window.cuurtSelToeGap = function (k) {
    ans.toeGap = k; ans.sizedUpForWidth = null;
    setTimeout(function () {
      if (TUNE.WIDTH_REASON_BANDS.indexOf(k) > -1) cuurtGoTo('whyroomy');
      else cuurtGoTo(5);
    }, 200);
  };
  window.cuurtSelWhyRoomy = function (v) {
    ans.sizedUpForWidth = (v === 'yes');
    setTimeout(function () { cuurtGoTo(5); }, 200);
  };
  window.cuurtSelWidth  = function (v) { ans.width = v; setTimeout(function () { cuurtGoTo(6); }, 200); };
  window.cuurtSelToe    = function (v) { ans.toe = v; setTimeout(function () { cuurtGoTo(7); }, 200); };
  window.cuurtSelUsage  = function (v) { ans.usage = v; setTimeout(function () { cuurtGoTo('result'); }, 200); };

  window.cuurtToggleCond = function (c) {
    var el = document.getElementById('cc_' + c);
    function clear(e) { e.style.borderColor = '#e2e8f0'; e.style.background = 'white'; e.dataset.sel = ''; }
    function set(e)   { e.style.borderColor = '#003C5F'; e.style.background = '#eaf4fb'; e.dataset.sel = '1'; }
    if (c === 'none') {
      ans.conditions = ['none'];
      document.querySelectorAll('[id^="cc_"]').forEach(clear);
      if (el) set(el);
      return;
    }
    var noneEl = document.getElementById('cc_none');
    if (noneEl) clear(noneEl);
    ans.conditions = ans.conditions.filter(function (x) { return x !== 'none'; });
    if (el && el.dataset.sel) { clear(el); ans.conditions = ans.conditions.filter(function (x) { return x !== c; }); }
    else if (el) { set(el); ans.conditions.push(c); }
  };

  /* ╔═══════════════════════════════════════════════════════════════════════╗
     ║  ENGINE v4                                                            ║
     ║                                                                       ║
     ║  Estimate the customer's FOOT, then pick the size whose clearance      ║
     ║  lands closest to TARGET, never below FLOOR, never above MAX.          ║
     ║                                                                       ║
     ║  v3 anchored on the customer's SHOE (required = their current insole). ║
     ║  That over-sized anyone whose current shoe had spare room, because it  ║
     ║  treated their whole insole as needed length. v4 asks how much room is ║
     ║  actually in front of the toes and works from the foot underneath.     ║
     ╚═══════════════════════════════════════════════════════════════════════╝ */

  var LADDER   = CUURT.slice().sort(function (a, b) { return a.len - b.len; });
  var SHORTEST = LADDER[0];
  var LONGEST  = LADDER[LADDER.length - 1];

  /* Load-time assertion: width can never move the pick by more than one size. */
  (function assertWidthLever() {
    var k = Object.keys(TUNE.WIDTH_EXTRA_MM);
    for (var i = 0; i < k.length; i++) {
      var v = TUNE.WIDTH_EXTRA_MM[k[i]];
      if (v < 0) throw new Error('CUURT config: WIDTH_EXTRA_MM.' + k[i] + ' is negative — width can never shorten a shoe.');
      if (v >= TUNE.SIZE_STEP_MM || v > TUNE.MAX_WIDTH_EXTRA_MM) {
        throw new Error('CUURT config: WIDTH_EXTRA_MM.' + k[i] + ' = ' + v +
          ' mm is >= one size step (' + TUNE.SIZE_STEP_MM + '). That breaks the ' +
          '"never more than +1 size" guarantee.');
      }
    }
    if (TUNE.MIN_CLEARANCE_MM >= TUNE.MAX_CLEARANCE_MM) throw new Error('CUURT config: floor >= max.');
    if (TUNE.HOUSE_TARGET_MM < TUNE.MIN_CLEARANCE_MM || TUNE.HOUSE_TARGET_MM > TUNE.MAX_CLEARANCE_MM) {
      throw new Error('CUURT config: HOUSE_TARGET_MM sits outside [MIN_CLEARANCE_MM, MAX_CLEARANCE_MM].');
    }
  })();

  function indexOfSize(s) { for (var i = 0; i < LADDER.length; i++) if (LADDER[i] === s) return i; return -1; }

  /* Smallest size at or above a required length. Used for the floor. */
  function ceilingSize(mm) {
    for (var i = 0; i < LADDER.length; i++) if (LADDER[i].len >= mm - TUNE.EPSILON_MM) return LADDER[i];
    return null;
  }

  /* Brand insole for a US size, interpolating between published rows. */
  function brandInternalFromUs(brand, usSize, gender) {
    var data = BD[brand] || BD['Other'];
    var tbl = (data[gender] && Object.keys(data[gender]).length) ? data[gender] : data['men'];
    if (tbl[usSize] !== undefined) return { mm: tbl[usSize], exact: true };
    var keys = Object.keys(tbl).map(Number).sort(function (a, b) { return a - b; });
    for (var i = 0; i < keys.length - 1; i++) {
      if (usSize > keys[i] && usSize < keys[i + 1]) {
        var t = (usSize - keys[i]) / (keys[i + 1] - keys[i]);
        return { mm: tbl[keys[i]] + t * (tbl[keys[i + 1]] - tbl[keys[i]]), exact: false };
      }
    }
    if (usSize < keys[0]) return { mm: tbl[keys[0]] - (keys[0] - usSize) * 8.467, exact: false };
    return { mm: tbl[keys[keys.length - 1]] + (usSize - keys[keys.length - 1]) * 8.467, exact: false };
  }

  /* Find the EU LABEL a given insole length carries in a brand's own ladder.
     Deriving position arithmetically from the value is circular — that bug
     silently disabled the ladder correction on the US path in v3.0. */
  function euNumberForMm(brand, mm) {
    var t = BDEU[brand]; if (!t) return null;
    var rows = t.u || t.men || t.women; if (!rows || !rows.length) return null;
    for (var i = 0; i < rows.length; i++) if (Math.abs(rows[i][2] - mm) < 0.05) return rows[i][1];
    for (var j = 0; j < rows.length - 1; j++) {
      if (mm > rows[j][2] && mm < rows[j + 1][2]) {
        var f = (mm - rows[j][2]) / (rows[j + 1][2] - rows[j][2]);
        return rows[j][1] + f * (rows[j + 1][1] - rows[j][1]);
      }
    }
    var span = (rows[rows.length - 1][2] - rows[0][2]) / (rows[rows.length - 1][1] - rows[0][1]);
    if (span <= 0) return null;
    if (mm < rows[0][2]) return rows[0][1] - (rows[0][2] - mm) / span;
    return rows[rows.length - 1][1] + (mm - rows[rows.length - 1][2]) / span;
  }

  /* Stretched-ladder guard. Returns max(tableMm, isoReconstruction) — up only. */
  function correctLadder(brand, euNumeric, tableMm) {
    if (!TUNE.LADDER_CORRECTION_ENABLED) return { mm: tableMm, corrected: 0 };
    if (TUNE.LADDER_TRUSTED_BRANDS.indexOf(brand) > -1) return { mm: tableMm, corrected: 0 };
    if (euNumeric == null || isNaN(euNumeric)) return { mm: tableMm, corrected: 0 };
    var t = BDEU[brand]; if (!t) return { mm: tableMm, corrected: 0 };
    var rows = t.u || t.men || t.women, anchor = null;
    for (var i = 0; i < rows.length; i++) {
      if (Math.abs(rows[i][1] - TUNE.LADDER_ANCHOR_EU) < 0.02) { anchor = rows[i]; break; }
    }
    if (!anchor) return { mm: tableMm, corrected: 0 };
    var iso = anchor[2] + (euNumeric - TUNE.LADDER_ANCHOR_EU) * TUNE.LADDER_TRUE_EU_STEP_MM;
    if (iso > tableMm) return { mm: iso, corrected: +(iso - tableMm).toFixed(2) };
    return { mm: tableMm, corrected: 0 };
  }

  /* ─────────────────────────────────────────────────────────────────────────
     estimateFoot(a) — turn the answers into ONE number: foot length in mm.
     ───────────────────────────────────────────────────────────────────────── */
  function estimateFoot(a) {
    /* PATH 1 — they measured it. Nothing to infer. */
    if (a.footMm) {
      return { foot: a.footMm, basis: 'measured', currentInsole: null, rawInsole: null,
               assumedGap: null, band: null, ladderCorrection: 0, exact: true };
    }

    /* PATH 2 — their current shoe, minus the room they report in front. */
    var insole, exact = true, euNumeric = null;
    if (a.euSel) { insole = a.euSel.mm; euNumeric = a.euSel.num; }
    else {
      if (!a.brand || a.usSize == null) return null;
      var look = brandInternalFromUs(a.brand, a.usSize, a.gender);
      insole = look.mm; exact = look.exact;
      euNumeric = euNumberForMm(a.brand, insole);
    }
    var fixed = correctLadder(a.brand, euNumeric, insole);
    var rawInsole = insole;
    insole = fixed.mm;

    var band = TUNE.TOE_GAP_BANDS[a.toeGap];
    if (!band) return null;

    /* The customer is somewhere unknown inside their band. Lean conservative
       by ASSUMED_GAP_SHIFT_MM (a smaller assumed gap => a longer foot estimate
       => a longer shoe), unless they told us they sized up for width on
       purpose, in which case they know their own gap and we trust the middle. */
    var mid = (band.lo + band.hi) / 2;
    var trust = TUNE.TRUST_MIDPOINT_IF_SIZED_UP_FOR_WIDTH && a.sizedUpForWidth === true;
    var assumedGap = trust ? mid : Math.max(band.lo, mid - TUNE.ASSUMED_GAP_SHIFT_MM);

    return { foot: insole - assumedGap, basis: 'inferred', currentInsole: insole,
             rawInsole: rawInsole, assumedGap: assumedGap, band: band,
             ladderCorrection: fixed.corrected, exact: exact };
  }

  /* ─────────────────────────────────────────────────────────────────────────
     recommend(a)
     ───────────────────────────────────────────────────────────────────────── */
  function recommend(a) {
    var est = estimateFoot(a);
    if (!est) return null;
    var foot = est.foot;

    /* Target: what we aim for. Only the toe-room preference moves it. */
    var toeDelta = TUNE.TOE_PREFERENCE_DELTA_MM[a.toe];
    if (toeDelta === undefined) toeDelta = 0;
    var target = TUNE.HOUSE_TARGET_MM + toeDelta;

    /* Bounds, in absolute mm. */
    var floorMm = foot + TUNE.MIN_CLEARANCE_MM;
    var maxMm   = foot + TUNE.MAX_CLEARANCE_MM + Math.max(0, toeDelta);

    var eligible = LADDER.filter(function (s) {
      return s.len >= floorMm - TUNE.EPSILON_MM && s.len <= maxMm + TUNE.EPSILON_MM;
    });

    var capped = false, runsRoomy = false;
    if (!eligible.length) {
      /* Nothing inside the window. Prefer a shoe that is too LONG over one
         that is too short — the long one is an annoyance, the short one is a
         return. Only when nothing is long enough do we cap and say so. */
      var above = LADDER.filter(function (s) { return s.len >= floorMm - TUNE.EPSILON_MM; });
      if (above.length) { eligible = [above[0]]; runsRoomy = true; }
      else { eligible = [LONGEST]; capped = true; }
    }

    /* THE CHOICE — closest clearance to target; ties go to the longer size.
       Note what this does at the boundary: with sizes 6.6 mm apart, it takes
       the larger size whenever the smaller would land below TARGET − 3.3 mm.
       So a 7 mm option is always rejected in favour of the 13.6 mm one. The
       engine's own pick therefore never sits below ~8.2 mm of clearance. */
    var primary = eligible[0], bestD = Infinity;
    eligible.forEach(function (s) {
      var d = Math.abs((s.len - foot) - target);
      if (d < bestD - TUNE.EPSILON_MM) { bestD = d; primary = s; }
      else if (Math.abs(d - bestD) <= TUNE.EPSILON_MM && s.len > primary.len) { primary = s; }
    });

    var lengthOnlySize = primary;

    /* WIDTH — step up one size only if the next still fits the window widened
       by the width request. Cannot move more than one size (asserted at load). */
    var widthExtra = TUNE.WIDTH_EXTRA_MM[a.width] || 0;
    var widthChangedSize = false, widthOutcome = widthExtra > 0 ? 'absorbed' : 'none';
    if (widthExtra > 0 && !capped && !runsRoomy) {
      var up = LADDER[indexOfSize(primary) + 1];
      if (up && (up.len - foot) <= TUNE.MAX_CLEARANCE_MM + widthExtra + Math.max(0, toeDelta) + TUNE.EPSILON_MM) {
        primary = up; widthChangedSize = true; widthOutcome = 'moved';
      } else if (up) { widthOutcome = 'declined'; }
      else { widthOutcome = 'topofrange'; }
    }

    var clearance = primary.len - foot;

    /* INVARIANT — the one rule that must never bend. */
    if (!capped && clearance < TUNE.MIN_CLEARANCE_MM - TUNE.EPSILON_MM) {
      throw new Error('CUURT engine invariant violated: ' + clearance.toFixed(2) +
                      ' mm of clearance is below the ' + TUNE.MIN_CLEARANCE_MM + ' mm floor');
    }

    /* SECOND OPTION — shown smaller, never as the headline. */
    var i = indexOfSize(primary);
    var upS = LADDER[i + 1], dnS = LADDER[i - 1];
    var secondary = null, secondaryKind = null;
    if (dnS && (dnS.len - foot) >= TUNE.MIN_CLEARANCE_MM - TUNE.EPSILON_MM) {
      secondary = dnS; secondaryKind = 'snugger';
    } else if (upS && (upS.len - foot) <= TUNE.MAX_CLEARANCE_MM + TUNE.SIZE_STEP_MM + TUNE.EPSILON_MM) {
      secondary = upS; secondaryKind = 'roomier';
    }

    var conf;
    if (capped) conf = 'capped';
    else if (runsRoomy) conf = 'floored';
    else if (est.basis === 'measured') conf = 'high';
    else if (a.brand === 'Other' || !est.exact) conf = 'medium';
    else conf = 'good';

    return {
      basis: est.basis, size: primary, foot: foot, clearance: clearance, target: target,
      floorMm: floorMm, maxMm: maxMm,
      currentInsole: est.currentInsole, rawInsole: est.rawInsole,
      assumedGap: est.assumedGap, band: est.band, toeGap: a.toeGap,
      sizedUpForWidth: a.sizedUpForWidth === true,
      ladderCorrection: est.ladderCorrection,
      lengthOnlySize: lengthOnlySize, widthExtra: widthExtra,
      widthChangedSize: widthChangedSize, widthOutcome: widthOutcome,
      secondary: secondary, secondaryKind: secondaryKind,
      capped: capped, runsRoomy: runsRoomy,
      runsLong: clearance >= TUNE.RUNS_LONG_NOTICE_MM,
      conf: conf
    };
  }

  function calcRec() { return recommend(ans); }

  /* ╔═══════════════════════════════════════════════════════════════════════╗
     ║  RESULT SCREEN                                                        ║
     ║  Two jobs: state the size, and connect the customer's condition to a  ║
     ║  concrete CUURT benefit.                                              ║
     ║                                                                       ║
     ║  COPY RULES — no exceptions:                                          ║
     ║   • No medical claims. Room, comfort, fit, natural movement only.     ║
     ║   • Never say a shoe treats, relieves, prevents or helps a condition. ║
     ║   • Describe the shoe, not the foot's medical situation.              ║
     ║   • Plain and confident. A knowledgeable friend, not a brochure.      ║
     ╚═══════════════════════════════════════════════════════════════════════╝ */

  /* Condition → benefit. Framed as room / comfort / fit, never as treatment.
     `rank` controls which lines survive when several are selected — the
     toe-box benefits lead because they are the most concrete and the most
     specific to CUURT. */
  var BENEFITS = {
    bunions: {
      rank: 1,
      label: 'Bunions',
      line: 'Our wide toe box gives the big-toe joint room, so there’s no squeeze or rub where a bunion sits.'
    },
    hammer: {
      rank: 2,
      label: 'Hammer / claw toes',
      line: 'The roomy toe box lets your toes lie naturally instead of being pushed together.'
    },
    neuropathy: {
      rank: 3,
      label: 'Neuropathy',
      /* Purely about space. No circulation or nerve claims — ever. */
      line: 'Nothing pinches across the forefoot — your foot gets room to sit naturally.'
    },
    flat: {
      rank: 4,
      label: 'Flat feet',
      line: 'The zero-drop, level platform lets your foot work the way it’s built to, instead of being propped up.'
    },
    plantar: {
      rank: 5,
      label: 'Plantar fasciitis',
      /* Deliberately neutral and non-clinical. Zero-drop is debated for this
         condition, so we describe the shoe and claim nothing about the foot. */
      line: 'A firm, stable, level base under the whole foot.'
    },
    none: {
      rank: 9,
      label: '',
      line: 'Room to splay, a level platform, a stable base — your foot moves the way it’s meant to.'
    }
  };

  function benefitLines(conditions) {
    var picked = (conditions || []).filter(function (c) { return BENEFITS[c] && c !== 'none'; });
    if (!picked.length) return [BENEFITS.none];
    picked.sort(function (a, b) { return BENEFITS[a].rank - BENEFITS[b].rank; });
    return picked.slice(0, TUNE.MAX_BENEFIT_LINES).map(function (c) { return BENEFITS[c]; });
  }

  function usLabel(row, gender) { return gender === 'women' ? row.usW : row.usM; }

  function renderResult() {
    var rec = calcRec();
    var body = document.getElementById('cuurtQuizBody');
    if (!rec || !body) { cuurtGoTo(1); return; }

    var p  = rec.size;
    var g  = ans.gender === 'women' ? 'women' : 'men';
    var us = usLabel(p, g);
    var genderWord = g === 'women' ? 'Women\u2019s' : 'Men\u2019s';
    /* CUURT is one unisex shoe, so the other gender's label is the SAME shoe.
       Showing it stops a mis-tapped step 1 from becoming a wrong order. */
    /* Women's labels stop at EU 45 on the published chart (and in the Shopify
       variants), so above that there is no women's equivalent to show. */
    var otherUs = (g === 'women') ? p.usM : p.usW;
    var otherLabel = (otherUs && otherUs !== '\u2014')
      ? 'Same shoe as ' + (g === 'women' ? 'Men\u2019s US ' : 'Women\u2019s US ') + otherUs + ' \u00b7 CUURT is unisex'
      : 'CUURT is unisex \u2014 one shoe, one last';

    /* ── Confidence badge — honest, never decorative ────────────────────── */
    var confMap = {
      high:   { bg: '#e8f5e9', col: '#2d7a3e', bord: '#c3dfc6', lbl: '✓ Measured fit — our most accurate result' },
      good:   { bg: '#e8f5e9', col: '#2d7a3e', bord: '#c3dfc6', lbl: '✓ Good match' },
      medium: { bg: '#fff8e1', col: '#b45309', bord: '#f5d97a', lbl: '~ Best estimate — measure your foot if you want certainty' },
      floored:{ bg: '#fff8e1', col: '#b45309', bord: '#f5d97a', lbl: '~ EU 36 is our smallest size — it will run roomy' },
      capped: { bg: '#fef2f2', col: '#991b1b', bord: '#f5c1c1', lbl: '△ Your foot is longer than our largest size — talk to us before ordering' }
    };
    var c = confMap[rec.conf] || confMap.good;

    /* ── "Why this size" — every number reproducible from the others ─────── */
    var why = [];
    var below = indexOfSize(p) > 0 ? LADDER[indexOfSize(p) - 1] : null;

    if (rec.basis === 'measured') {
      why.push('<strong>Your foot:</strong> ' + (rec.foot / 10).toFixed(1) + ' cm = ' +
        rec.foot.toFixed(0) + ' mm. EU ' + p.eu + ' is ' + p.len.toFixed(1) + ' mm inside, so ' +
        '<span style="white-space:nowrap;">' + p.len.toFixed(1) + ' − ' + rec.foot.toFixed(0) +
        ' = ' + rec.clearance.toFixed(1) + ' mm</span> sits in front of your longest toe.');
    } else {
      var sizeLbl = ans.euSel ? ('EU ' + ans.euSel.label) : ('US ' + ans.usSize);
      var cur = rec.currentInsole;
      why.push('<strong>Working out your foot:</strong> your ' + ans.brand + ' ' + sizeLbl +
        ' is ' + cur.toFixed(1) + ' mm of insole, and you said ' + rec.band.label.toLowerCase() +
        ' in front. <span style="white-space:nowrap;">' + cur.toFixed(1) + ' − ' +
        rec.assumedGap.toFixed(1) + ' = ' + rec.foot.toFixed(1) + ' mm</span> of foot.');

      if (rec.ladderCorrection > 0) {
        why.push('<strong>On that ' + cur.toFixed(1) + ' mm:</strong> ' + ans.brand +
          '’s chart lists ' + rec.rawInsole.toFixed(1) + ' mm for ' + sizeLbl +
          ', but their size ladder is stretched at this end, so we read it as ' +
          cur.toFixed(1) + ' mm (+' + rec.ladderCorrection.toFixed(1) +
          ' mm). We correct upward only — never down.');
      }

      why.push('<strong>Your size:</strong> EU ' + p.eu + ' is ' + p.len.toFixed(1) +
        ' mm inside. <span style="white-space:nowrap;">' + p.len.toFixed(1) + ' − ' +
        rec.foot.toFixed(1) + ' = ' + rec.clearance.toFixed(1) + ' mm</span> in front of your toes' +
        (rec.clearance >= 10 && rec.clearance <= 13 ? ' — right in the ideal 10–13 mm range.' : '.'));
    }

    if (below && !rec.capped) {
      var bc = below.len - rec.foot;
      if (bc < TUNE.MIN_CLEARANCE_MM) {
        why.push('<strong>Why not EU ' + below.eu + ':</strong> it would leave only ' +
          bc.toFixed(1) + ' mm in front of your toes, under our ' + TUNE.MIN_CLEARANCE_MM +
          ' mm minimum. We never go below that — a few mm long is an annoyance, short is a return.');
      } else {
        why.push('<strong>Why not EU ' + below.eu + ':</strong> ' + bc.toFixed(1) +
          ' mm is further from the ' + rec.target.toFixed(1) + ' mm we aim for than ' +
          rec.clearance.toFixed(1) + ' mm is. It is wearable — just snugger.');
      }
    }

    if (rec.basis === 'inferred' && rec.currentInsole > p.len + 0.5) {
      why.push('<strong>Yes, that is shorter than your ' + ans.brand + ':</strong> ' +
        p.len.toFixed(1) + ' mm inside versus ' + rec.currentInsole.toFixed(1) + ' mm. ' +
        (rec.sizedUpForWidth
          ? 'You sized up for width, so the extra length was never length you needed. Our toe box is wide at your true size.'
          : 'The room you reported is room you were not using. This puts the shoe on your foot, not on your old shoe.'));
    }

    var nextUp = LADDER[Math.min(indexOfSize(p) + 1, LADDER.length - 1)];
    var widthGain = (nextUp.wid - p.wid).toFixed(1);
    var widthWord = ans.width === 'very-wide' ? 'Very wide' : 'Wide';
    if (rec.widthOutcome === 'moved') {
      why.push('<strong>' + widthWord + ':</strong> length alone put you at EU ' + rec.lengthOnlySize.eu +
        ', but you were near the top of it, so the ' + rec.widthExtra + ' mm we add for a ' +
        (ans.width === 'very-wide' ? 'very wide' : 'wide') + ' foot moved you to EU ' + p.eu +
        '. Forefoot goes ' + rec.lengthOnlySize.wid.toFixed(1) + ' mm → ' + p.wid.toFixed(1) + ' mm.');
    } else if (rec.widthOutcome === 'absorbed') {
      why.push('<strong>' + widthWord + ':</strong> we added ' + rec.widthExtra +
        ' mm to your requirement and EU ' + p.eu + ' still covered it. Going up anyway costs ' +
        TUNE.SIZE_STEP_MM + ' mm of length for ' + widthGain + ' mm of forefoot.');
    } else if (rec.widthOutcome === 'declined') {
      why.push('<strong>' + widthWord + ':</strong> we checked EU ' + nextUp.eu +
        ' and turned it down — ' + (nextUp.len - rec.foot).toFixed(0) +
        ' mm in front of your toes to buy ' + widthGain +
        ' mm of forefoot. On a court shoe that trade loses more than it gains.');
    } else if (ans.width === 'narrow') {
      why.push('<strong>Narrow:</strong> the length is right for you. Expect more room across the forefoot than you’re used to — that’s the shoe, not the size.');
    }

    if (ans.width === 'very-wide' && rec.widthOutcome !== 'moved') {
      why.push('<strong>Straight answer on width:</strong> CUURT comes in one width. Each size up adds ' +
        widthGain + ' mm across the forefoot but ' + TUNE.SIZE_STEP_MM +
        ' mm of length, so buying length to get width stops working fast. EU ' + p.eu + ' at ' +
        p.wid.toFixed(1) + ' mm is the widest we can give you at a length that still fits.');
    }

    if (rec.runsRoomy) {
      why.push('<strong>Heads up:</strong> EU ' + p.eu + ' is our smallest size and will feel roomy on you — ' +
        rec.clearance.toFixed(0) + ' mm in front. A thicker court sock takes up the difference.');
    } else if (rec.runsLong && !rec.capped) {
      why.push('<strong>Heads up:</strong> ' + rec.clearance.toFixed(1) +
        ' mm is at the roomy end of what we’d recommend. The size down would drop under our ' +
        TUNE.MIN_CLEARANCE_MM + ' mm minimum, so this is still the right call — wear a proper court sock.');
    }

    var whyHtml = why.map(function (r) {
      return '<div style="display:flex;gap:10px;align-items:flex-start;">' +
        '<div style="width:18px;height:18px;background:#2d7a3e;border-radius:50%;display:flex;align-items:center;justify-content:center;flex-shrink:0;margin-top:2px;">' +
        '<svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="3"><path d="M5 13l4 4L19 7"/></svg></div>' +
        '<div style="font-size:13px;color:#1a1a2e;line-height:1.45;text-align:left;">' + r + '</div></div>';
    }).join('');

    /* ── Condition → benefit block ──────────────────────────────────────── */
    var bens = benefitLines(ans.conditions);
    var benHtml = bens.map(function (b) {
      return '<div style="display:flex;gap:10px;align-items:flex-start;">' +
        '<span style="color:#00A8E8;font-size:16px;line-height:1.2;flex-shrink:0;">●</span>' +
        '<div style="font-size:13px;color:#1a1a2e;line-height:1.5;text-align:left;">' +
        (b.label ? '<strong>' + b.label + ':</strong> ' : '') + b.line + '</div></div>';
    }).join('');

    var benTitle = (ans.conditions && ans.conditions.length && ans.conditions.indexOf('none') === -1)
      ? 'What this means for your feet'
      : 'What you’re getting';

    /* ── Usage line ─────────────────────────────────────────────────────── */
    var uNotes = {
      pickleball: 'Built for the quick lateral cuts and pivots of pickleball',
      tennis:     'Engineered for the stop-start demands of tennis',
      padel:      'Ideal for the short explosive movements of padel & squash',
      training:   'Great for high-rep drills and intensive court practice',
      multi:      'A versatile fit for players who mix court sports'
    };
    var subLine = uNotes[ans.usage] || 'Measured from your foot, not guessed from your size';
    if (ans.usage && ans.usage.indexOf('custom:') === 0) {
      subLine = 'A wide toe box to keep you comfortable during ' + ans.usage.slice(7);
    }

    /* ── Render ─────────────────────────────────────────────────────────── */
    var h = '<div style="padding:32px 24px 24px;text-align:center;">';
    /* Icon sits beside the title rather than stacked above it — the old layout
       burned ~90px of vertical space before the customer saw their size. */
    h += '<div style="display:flex;align-items:center;justify-content:center;gap:14px;margin-bottom:6px;">';
    h += '<div style="width:44px;height:44px;background:#003C5F;border-radius:50%;display:flex;align-items:center;justify-content:center;flex-shrink:0;"><svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg></div>';
    h += '<div style="font-size:26px;font-weight:800;color:#1a1a2e;line-height:1.1;text-align:left;">Your CUURT size</div>';
    h += '</div>';
    h += '<div style="font-size:13px;color:#6b7280;margin-bottom:22px;text-align:center;">' + subLine + '</div>';

    h += '<div style="background:#f0f7ee;border:1.5px solid #c3dfc6;border-radius:12px;padding:20px;margin-bottom:16px;text-align:left;">';
    h += '<div style="font-size:11px;font-weight:700;letter-spacing:1.5px;text-transform:uppercase;color:#2d7a3e;margin-bottom:8px;">WE RECOMMEND</div>';
    /* The headline is whatever the customer actually shops in, taken from the
       unit they picked in step 3:
         US → gendered US big, EU small   (~90% of traffic)
         UK → UK big, EU small
         EU → EU alone; a US number next to it is just noise to them
       The measured-foot path has no unit, so it defaults to the US treatment. */
    var unit = (ans.euSel || ans.usSize != null) ? ans.unit : 'us';
    var ukLbl = p.uk;   /* from the published chart, never derived */
    var bigTxt, smallTxt, kickerTxt, subTxt = null;
    if (unit === 'eu') {
      bigTxt = 'EU ' + p.eu; smallTxt = ''; kickerTxt = '';
    } else if (unit === 'uk') {
      bigTxt = 'UK ' + ukLbl; smallTxt = 'EU ' + p.eu; kickerTxt = '';
    } else if (!us || us === '\u2014') {
      /* No US label exists for this size in this gender (women's above EU 45).
         Lead with EU rather than printing "US —". */
      bigTxt = 'EU ' + p.eu; smallTxt = 'US Men ' + p.usM; kickerTxt = '';
      subTxt = 'Our Women’s US sizing stops below this — CUURT is unisex, so this is the same shoe.';
    } else {
      bigTxt = 'US ' + us; smallTxt = 'EU ' + p.eu; kickerTxt = genderWord;
    }
    if (kickerTxt) {
      h += '<div style="font-size:15px;font-weight:700;color:#2d7a3e;line-height:1;letter-spacing:0.5px;text-transform:uppercase;margin-bottom:6px;">' + kickerTxt + '</div>';
    }
    h += '<div style="display:flex;align-items:baseline;gap:12px;margin-bottom:6px;flex-wrap:wrap;">';
    h += '<div style="font-size:54px;font-weight:800;color:#2d7a3e;line-height:1;">' + bigTxt + '</div>';
    if (smallTxt) h += '<div style="font-size:24px;font-weight:700;color:#2d7a3e;opacity:0.7;line-height:1;">' + smallTxt + '</div>';
    h += '</div>';
    h += '<div style="font-size:13px;color:#6b7280;margin-bottom:10px;font-weight:500;">' +
         (subTxt || (unit === 'us' ? otherLabel : 'CUURT is unisex — one shoe, one last')) + '</div>';
    h += '<div style="display:flex;gap:8px;margin-bottom:14px;flex-wrap:wrap;">';
    h += '<span style="background:white;border:1px solid #e2e8f0;border-radius:20px;padding:4px 12px;font-size:12px;font-weight:600;color:#003C5F;">Inside: ' + fmtLen(p.len, unit) + '</span>';
    h += '<span style="background:white;border:1px solid #e2e8f0;border-radius:20px;padding:4px 12px;font-size:12px;font-weight:600;color:#003C5F;">Forefoot: ' + fmtLen(p.wid, unit) + '</span>';
    /* ONE number with ONE meaning on every path: room in front of the toes.
       Earlier versions printed "+X mm room" where X meant something different
       on the measured path than on the brand path, which is what made the card
       fail to add up. There is now nothing to confuse it with. */
    if (!rec.capped) {
      h += '<span style="background:white;border:1px solid #e2e8f0;border-radius:20px;padding:4px 12px;font-size:12px;font-weight:600;color:#2d7a3e;">' +
           fmtLen(rec.clearance, unit) + ' in front of your toes</span>';
    }
    h += '</div>';
    h += '<div style="display:inline-flex;align-items:center;gap:6px;border-radius:20px;padding:6px 14px;font-size:13px;font-weight:600;margin-bottom:16px;background:' + c.bg + ';color:' + c.col + ';border:1px solid ' + c.bord + ';">' + c.lbl + '</div>';
    h += '<div style="background:white;border-radius:8px;padding:14px;display:flex;flex-direction:column;gap:10px;">' + whyHtml + '</div>';
    h += '</div>';

    /* Condition → benefit card */
    h += '<div style="background:white;border:1px solid #e2e8f0;border-radius:12px;padding:16px 18px;margin-bottom:20px;">';
    h += '<div style="font-size:11px;font-weight:700;letter-spacing:1.5px;text-transform:uppercase;color:#003C5F;margin-bottom:12px;text-align:left;">' + benTitle + '</div>';
    h += '<div style="display:flex;flex-direction:column;gap:10px;">' + benHtml + '</div>';
    h += '</div>';

    h += '<div style="margin-top:16px;padding:12px 16px;background:rgba(0,168,232,0.12);border-radius:8px;font-size:12px;color:#003C5F;line-height:1.5;text-align:left;">Every CUURT is built on a wide toe box and a zero-drop sole. If your current shoes feel narrow or tilted forward, this will feel different at first — that’s the design, not the size.</div>';
    h += '</div>';
    body.innerHTML = h;

    /* ── PINNED ACTION BAR ────────────────────────────────────────────────
       The guarantees and the CTA used to sit at the bottom of a long scrolling
       result, so on a phone the customer had to scroll past the entire
       explanation before they could buy. They now live in the modal footer,
       which is flex-shrink:0 and therefore always on screen. */
    var footerEl = document.getElementById('cuurtQuizFooter');
    var slotEl   = document.getElementById('cuurtFooterExtra');
    if (footerEl && slotEl) {
      footerEl.style.display = '';
      footerEl.style.padding = '10px 16px 12px';
      var pb = footerEl.querySelector('button');
      if (pb) pb.style.display = 'none';
      var bar = '';
      bar += '<div style="display:grid;grid-template-columns:repeat(3,1fr);gap:1px;background:#e2e8f0;border:1px solid #e2e8f0;border-radius:10px;overflow:hidden;margin-bottom:10px;">';
      [['100 days','Fit guarantee'],['Free','Size exchanges'],['6 months','Sole warranty']].forEach(function (t) {
        bar += '<div style="background:white;padding:8px 4px;text-align:center;"><div style="font-size:14px;font-weight:800;color:#003C5F;line-height:1;margin-bottom:2px;">' + t[0] + '</div><div style="font-size:10px;color:#6b7280;line-height:1.2;">' + t[1] + '</div></div>';
      });
      bar += '</div>';
      bar += '<button onclick="cuurtSelectSize()" type="button" style="width:100%;padding:15px;background:#003C5F;color:white;border:none;border-radius:12px;font-size:15px;font-weight:700;letter-spacing:0.4px;cursor:pointer;display:flex;align-items:center;justify-content:center;gap:8px;text-transform:uppercase;font-family:inherit;">';
      bar += '<svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2.5"><path d="M5 13l4 4L19 7"/></svg>';
      bar += 'Select ' + (kickerTxt ? kickerTxt + ' ' : '') + bigTxt + (smallTxt ? '  ·  ' + smallTxt : '') + '</button>';
      bar += '<div style="text-align:center;margin-top:8px;"><button onclick="cuurtOpenQuiz()" type="button" style="background:none;border:none;cursor:pointer;font-size:12px;color:#6b7280;text-decoration:underline;text-underline-offset:2px;font-family:inherit;">Retake quiz</button></div>';
      slotEl.innerHTML = bar;
    }

    /* The same words the "Select …" button uses, so the page's trigger button
       and the modal never disagree about what was recommended. */
    rec.display = (kickerTxt ? kickerTxt + ' ' : '') + bigTxt + (smallTxt ? ' · ' + smallTxt : '');
    if (typeof window.cuurtPaintTriggers === 'function') {
      try { window.cuurtPaintTriggers(rec.display); } catch (e) {}
    }

    window.__cuurtLastRec = rec;
    track('cuurt_quiz_complete', payload(rec));
  }

  /* ── SELECT + DATA CAPTURE ───────────────────────────────── */
  function payload(rec) {
    return {
      quiz_path: ans.path,
      brand: ans.brand, unit: ans.unit, us_size: ans.usSize,
      eu_size: ans.euSel ? ans.euSel.label : null, gender: ans.gender,
      width: ans.width, toe: ans.toe,
      conditions: (ans.conditions || []).join('|'), usage: ans.usage,
      foot_mm: ans.footMm || null,
      /* Engine internals — logged so quiz → order → exchange can be joined and
         the never-short rule can be audited against real return data. */
      /* Engine internals — logged so quiz → order → exchange can be joined and
         the floor can be audited against real return data. */
      basis:            rec ? rec.basis : null,
      toe_gap_band:     rec ? rec.toeGap : null,
      assumed_gap_mm:   rec && rec.assumedGap != null ? rec.assumedGap : null,
      sized_up_for_width: rec ? rec.sizedUpForWidth : null,
      current_insole:   rec && rec.currentInsole ? +rec.currentInsole.toFixed(1) : null,
      raw_insole:       rec && rec.rawInsole ? +rec.rawInsole.toFixed(1) : null,
      ladder_correction_mm: rec ? rec.ladderCorrection : null,
      foot_mm_est:      rec ? +rec.foot.toFixed(1) : null,
      target_mm:        rec ? rec.target : null,
      rec_eu:           rec ? rec.size.eu : null,
      rec_us_m:         rec ? rec.size.usM : null,
      rec_us_w:         rec ? rec.size.usW : null,
      rec_internal_mm:  rec ? rec.size.len : null,
      clearance_mm:     rec ? +rec.clearance.toFixed(1) : null,
      width_extra_mm:   rec ? rec.widthExtra : null,
      width_outcome:    rec ? rec.widthOutcome : null,
      secondary_eu:     rec && rec.secondary ? rec.secondary.eu : null,
      secondary_kind:   rec ? rec.secondaryKind : null,
      capped:           rec ? rec.capped : null,
      runs_roomy:       rec ? rec.runsRoomy : null,
      confidence:       rec ? rec.conf : null,
      engine: 'v4-foot-anchored',
      ts: new Date().toISOString()
    };
  }

  function track(event, data) {
    try { (window.dataLayer = window.dataLayer || []).push(Object.assign({ event: event }, data)); } catch (e) {}
    try { localStorage.setItem('cuurt_quiz_last', JSON.stringify(Object.assign({ event: event }, data))); } catch (e) {}
  }

  /* ╔═══════════════════════════════════════════════════════════════════════╗
     ║  STORE INTEGRATION                                                    ║
     ║                                                                       ║
     ║  The quiz recommends a CUURT EU size. The store sells variants with    ║
     ║  whatever labels you gave them — which are NOT the same strings.       ║
     ║  A live CUURT product page shows W 6 / W 7 / W 7.5 / W 8-8.5 /         ║
     ║  W 9-9.5 / W 10-10.5 ... — ten range labels for thirteen EU sizes.     ║
     ║  Text matching "W 10" against "W 10-10.5" fails, so the mapping is     ║
     ║  DECLARED, not guessed. Fill in window.CUURT_CONFIG (see below) and    ║
     ║  run cuurtScanVariants() in the console to generate a starter map.     ║
     ╚═══════════════════════════════════════════════════════════════════════╝ */

  function cfg() { return window.CUURT_CONFIG || {}; }

  /* What this EU size is called on THIS store. */
  function variantLabelFor(rec) {
    var C = cfg(), g = ans.gender === 'women' ? 'women' : 'men';
    var byGender = g === 'women' ? C.variantByEuWomen : C.variantByEuMen;
    if (byGender && byGender[rec.size.eu] != null) return String(byGender[rec.size.eu]);
    if (C.variantByEu && C.variantByEu[rec.size.eu] != null) return String(C.variantByEu[rec.size.eu]);
    if (typeof C.variantLabel === 'function') return String(C.variantLabel(rec, ans));
    /* No map configured — fall back to the plain gendered US label. Works only
       if your variants are named that way; cuurtScanVariants() will tell you. */
    return (g === 'women' ? 'W ' : 'M ') + usLabel(rec.size, g);
  }
  function variantIdFor(rec) {
    var C = cfg(), g = ans.gender === 'women' ? 'women' : 'men';
    var byGender = g === 'women' ? C.variantIdByEuWomen : C.variantIdByEuMen;
    if (byGender && byGender[rec.size.eu]) return byGender[rec.size.eu];
    if (C.variantIdByEu && C.variantIdByEu[rec.size.eu]) return C.variantIdByEu[rec.size.eu];
    return null;
  }

  var norm = function (t) { return String(t == null ? '' : t).replace(/\s+/g, ' ').trim().toLowerCase(); };

  /* Every control a theme or page builder might render size options as.

     TWO THINGS THIS GETS RIGHT that the first version didn't:

     1. optionSelector NARROWS the search; it must never eliminate it. A landing
        page built in a page builder has none of the standard theme classes, so
        the selector matched nothing, the scope became empty, and the search
        found nothing to click — on a page whose labels matched the map exactly.
        An unmatched selector now falls back to the whole document.

     2. Page builders render swatches as <div>/<span>, not <button>/<input>.
        The candidate set has to include generic elements — which is only safe
        because of the ranking in findControl(): an element that CONTAINS a
        better match never wins. */
  var CTRL_SELECTOR =
    'input[type="radio"], select, button, label, a, li, ' +
    '[data-value], [data-option-value], [data-variant-option], [data-option], ' +
    '[role="radio"], [role="option"], div, span';

  function optionControls() {
    var C = cfg();
    var roots = [];
    if (C.optionSelector) {
      var found = document.querySelectorAll(C.optionSelector);
      if (found.length) roots = Array.prototype.slice.call(found);
    }
    if (!roots.length) roots = [document];       /* ← never search nothing */
    var out = [], seen = [];
    roots.forEach(function (root) {
      if (!root || !root.querySelectorAll) return;
      Array.prototype.forEach.call(root.querySelectorAll(CTRL_SELECTOR), function (el) {
        if (seen.indexOf(el) < 0) { seen.push(el); out.push(el); }
      });
    });
    return out;
  }

  /* Text a control presents to the shopper. */
  function controlLabel(el) {
    if (el.tagName === 'SELECT') return null;
    var v = el.getAttribute && (el.getAttribute('data-value') ||
                                el.getAttribute('data-option-value') ||
                                el.getAttribute('data-variant-option'));
    if (v) return v;
    if (el.tagName === 'INPUT') {
      var lab = el.id ? document.querySelector('label[for="' + el.id + '"]') : null;
      return (lab && lab.textContent) || el.value ||
             (el.getAttribute && el.getAttribute('aria-label'));
    }
    return el.textContent || (el.getAttribute && el.getAttribute('aria-label'));
  }

  /* Themes append availability to the visible text — "US Men 12Sold out". The
     size is still the size, so strip the noise before comparing. */
  var AVAIL = /\b(sold\s*out|out\s*of\s*stock|unavailable|coming\s*soon|notify\s*me)\b/g;
  function stripAvail(t) { return norm(t).replace(AVAIL, '').replace(/\s+/g, ' ').trim(); }

  /* Rank a candidate against the wanted label. Lower is better; null = no match.
     Tier 1 exact · 2 exact once availability text is stripped · 3 starts with.
     "Starts with" cannot select the WRONG size — every candidate must contain
     the complete wanted label — but it CAN match an outer container that holds
     the whole list, so ties break on the shortest text. */
  function matchTier(el, want) {
    var raw = controlLabel(el);
    if (!raw) return null;
    var n = norm(raw);
    if (!n) return null;
    if (n === want) return 1;
    if (stripAvail(n) === want) return 2;
    if (n.indexOf(want) === 0) return 3;
    return null;
  }

  var INTERACTIVE = ['INPUT', 'BUTTON', 'LABEL', 'A'];

  /* Best control for `want`, or null. Prefers: better tier → an element with no
     matching descendant → shorter text → a genuinely clickable tag. */
  function findControl(want) {
    var best = null, bestKey = null;
    optionControls().forEach(function (el) {
      if (el.tagName === 'SELECT') return;
      var tier = matchTier(el, want);
      if (tier == null) return;
      var len = norm(el.textContent || controlLabel(el) || '').length;
      var key = [tier, len, INTERACTIVE.indexOf(el.tagName) < 0 ? 1 : 0];
      if (!best || key[0] < bestKey[0] ||
         (key[0] === bestKey[0] && key[1] < bestKey[1]) ||
         (key[0] === bestKey[0] && key[1] === bestKey[1] && key[2] < bestKey[2])) {
        best = el; bestKey = key;
      }
    });
    return best;
  }

  /* Did the click actually land? Not every theme exposes this, so "unknown" is
     a legitimate answer — but a confident NO is worth reporting. */
  function looksSelected(el) {
    if (!el) return null;
    var probe = el;
    if (el.tagName !== 'INPUT') {
      var inp = el.querySelector && el.querySelector('input[type="radio"]');
      if (!inp && el.getAttribute && el.getAttribute('for')) {
        inp = document.getElementById(el.getAttribute('for'));
      }
      if (inp) probe = inp;
    }
    if (probe.tagName === 'INPUT') return !!probe.checked;
    var aria = el.getAttribute && (el.getAttribute('aria-checked') || el.getAttribute('aria-selected'));
    if (aria != null) return aria === 'true';
    var cls = ' ' + (el.className && el.className.baseVal !== undefined
                     ? el.className.baseVal : (el.className || '')) + ' ';
    if (/\b(selected|active|is-selected|is-active|checked)\b/.test(cls)) return true;
    return null;   /* can't tell */
  }

  function fireChange(el) {
    ['input', 'change'].forEach(function (t) {
      try { el.dispatchEvent(new Event(t, { bubbles: true })); } catch (e) {}
    });
  }

  /* ── The actual selection. Returns {method, verified} or null. ───────────

     ORDER MATTERS. The click and <select> routes drive the page's own UI, so
     the swatch the shopper sees and the variant in the form stay in agreement.
     Writing a variant id into the hidden input does NOT repaint a custom
     swatch UI — the page would show one size and add another to the cart, which
     is worse than not selecting at all. So the id route is now a LAST resort,
     not a first one.                                                        */
  function selectVariantOnPage(rec) {
    var want = norm(variantLabelFor(rec));
    var wantId = variantIdFor(rec);

    /* 1. A <select> holding the size option — searched document-wide. */
    var sels = document.querySelectorAll('select');
    for (var i = 0; i < sels.length; i++) {
      for (var j = 0; j < sels[i].options.length; j++) {
        var ot = norm(sels[i].options[j].text), ov = norm(sels[i].options[j].value);
        if (ot === want || ov === want || stripAvail(ot) === want) {
          sels[i].selectedIndex = j; fireChange(sels[i]);
          return { method: 'select', verified: norm(sels[i].options[sels[i].selectedIndex].text).indexOf(want) === 0 };
        }
      }
    }

    /* 2. The best-matching clickable control. */
    var el = findControl(want);
    if (el) {
      if (el.tagName === 'INPUT') { el.checked = true; fireChange(el); }
      try { el.click(); } catch (e) {}
      /* A radio is usually visually hidden behind its label — click that too,
         because many themes bind their handler to the label, not the input. */
      if (el.tagName === 'INPUT' && el.id) {
        var lb = document.querySelector('label[for="' + el.id + '"]');
        if (lb) { try { lb.click(); } catch (e) {} }
      } else {
        var inner = el.querySelector && el.querySelector('input[type="radio"], button, label');
        if (inner) { try { inner.click(); } catch (e) {} }
      }
      fireChange(el);
      return { method: 'click', verified: looksSelected(el) };
    }

    /* 3. Last resort: the variant id. Keeps the form correct even where the
          visible UI can't be driven — flagged as unverified on purpose. */
    if (wantId) {
      var hidden = document.querySelector('input[name="id"], select[name="id"]');
      if (hidden) { hidden.value = String(wantId); fireChange(hidden); }
      try {
        var u = new URL(window.location.href);
        u.searchParams.set('variant', String(wantId));
        window.history.replaceState({}, '', u.toString());
      } catch (e) {}
      if (hidden) return { method: 'variant-id', verified: null };
    }
    return null;
  }

  /* Optional "Recommended Size: X" banner, like the one on your PDP.
     Any element with data-cuurt-recommended gets filled. */
  function paintBanner(rec) {
    var label = variantLabelFor(rec);
    var els = document.querySelectorAll('[data-cuurt-recommended]');
    Array.prototype.forEach.call(els, function (el) {
      el.textContent = (el.getAttribute('data-cuurt-prefix') || 'Recommended Size: ') + label;
      el.style.display = '';
    });
    try { localStorage.setItem('cuurt_recommended_label', label); } catch (e) {}
    return els.length;
  }
  /* Repaint on load so the banner survives a page refresh. */
  function restoreBanner() {
    try {
      var v = localStorage.getItem('cuurt_recommended_label');
      if (!v) return;
      Array.prototype.forEach.call(document.querySelectorAll('[data-cuurt-recommended]'), function (el) {
        el.textContent = (el.getAttribute('data-cuurt-prefix') || 'Recommended Size: ') + v;
        el.style.display = '';
      });
    } catch (e) {}
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', restoreBanner);
  else restoreBanner();

  window.cuurtSelectSize = function () {
    var rec = window.__cuurtLastRec;
    if (!rec) return;
    var pl = payload(rec);
    track('cuurt_quiz_select_size', pl);

    /* Ride along with the order so quiz → order → exchange can be joined. */
    try {
      if (typeof fetch === 'function' && (window.Shopify || /\.myshopify\.com|cuurt/i.test(location.hostname))) {
        fetch('/cart/update.js', {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ attributes: {
            'Size quiz result': 'EU ' + pl.rec_eu + ' / US M ' + pl.rec_us_m + ' (' + pl.confidence + ', ' + pl.clearance_mm + ' mm clearance)',
            'Size quiz data': JSON.stringify(pl)
          } })
        }).catch(function () {});
      }
    } catch (e) {}

    paintBanner(rec);

    /* Your own hook wins over everything, if you define one. */
    if (typeof window.cuurtOnSizeSelected === 'function') {
      try { window.cuurtOnSizeSelected(rec, ans, variantLabelFor(rec)); } catch (e) {}
      cuurtCloseQuiz();
      return;
    }

    var r = null;
    try { r = selectVariantOnPage(rec); } catch (e) {}
    if (!r && window.console && console.warn) {
      console.warn('[CUURT] Could not find "' + variantLabelFor(rec) + '" among the size ' +
        'controls on this page.\nRun  cuurtScanVariants()  in this console — it prints every ' +
        'size-like label it can see and how it is marked up.');
    } else if (r && r.verified === false && window.console && console.warn) {
      console.warn('[CUURT] Clicked the control for "' + variantLabelFor(rec) +
        '" but the page does not show it as selected. The theme may bind its handler ' +
        'elsewhere. Run  cuurtScanVariants()  and send me the output.');
    }
    track('cuurt_quiz_variant_selected', {
      method: (r && r.method) || 'failed',
      verified: r ? String(r.verified) : 'false',
      label: variantLabelFor(rec), rec_eu: pl.rec_eu
    });
    cuurtCloseQuiz();
  };

  /* ── Setup helper. Run in the console on a product page. ───────────────── */
  window.cuurtScanVariants = function () {
    var C = cfg();
    var mapped = C.variantByEu || {};
    var wanted = Object.keys(mapped).map(function (k) { return norm(mapped[k]); });

    /* What does each mapped label actually resolve to on THIS page? That is the
       question worth answering — a list of labels only tells you the map is
       right, which it usually is. */
    var rows = [];
    Object.keys(mapped).forEach(function (eu) {
      var want = norm(mapped[eu]);
      var el = findControl(want), inSelect = null;
      var sels = document.querySelectorAll('select');
      for (var i = 0; i < sels.length && !inSelect; i++) {
        for (var j = 0; j < sels[i].options.length; j++) {
          if (norm(sels[i].options[j].text) === want) { inSelect = sels[i]; break; }
        }
      }
      rows.push({
        EU: eu,
        label: mapped[eu],
        found: !!(el || inSelect),
        via: inSelect ? 'select' : (el ? el.tagName.toLowerCase() : '—'),
        tier: el ? matchTier(el, want) : (inSelect ? 1 : null),
        classes: el && el.className ? String(el.className).slice(0, 60) : ''
      });
    });

    var missing = rows.filter(function (r) { return !r.found; });
    console.log('[CUURT] Can each mapped size be found on this page?');
    (console.table ? console.table : console.log)(rows);
    console.log(missing.length
      ? '✗ ' + missing.length + ' of ' + rows.length + ' NOT found — the labels below do not exist on this page.'
      : '✓ all ' + rows.length + ' mapped sizes found.');

    /* Also dump every size-like label present, so a mismatch is obvious. */
    var seen = [];
    optionControls().forEach(function (el) {
      if (el.tagName === 'SELECT') {
        for (var i = 0; i < el.options.length; i++) {
          var t = el.options[i].text.replace(/\s+/g, ' ').trim();
          if (t && seen.indexOf(t) < 0) seen.push(t);
        }
        return;
      }
      var t = (controlLabel(el) || '').replace(/\s+/g, ' ').trim();
      if (t && t.length < 40 && /\d/.test(t) && seen.indexOf(t) < 0) seen.push(t);
    });
    console.log('[CUURT] every size-like label on the page:', seen);
    if (C.optionSelector) {
      var n = document.querySelectorAll(C.optionSelector).length;
      console.log('[CUURT] optionSelector "' + C.optionSelector + '" matches ' + n +
        ' element(s)' + (n ? '.' : ' — falling back to a whole-document search.'));
    }
    return { rows: rows, labelsOnPage: seen, missing: missing.map(function (r) { return r.label; }) };
  };

  /* ── THE TRIGGER BUTTON REFLECTS THE RESULT ─────────────────────────────
     Once a size is recommended, the button that opened the quiz should stop
     saying "Find my Perfect Size" and start saying what the answer was — with a
     way back in underneath it. Nothing to add to the page: any button whose
     onclick calls cuurtOpenQuiz is picked up automatically, and anything
     carrying data-cuurt-trigger is too.

     The original label and markup are stashed on the element, so "Retake quiz"
     can put it back exactly as the theme rendered it. */
  var TICK = '<svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" ' +
             'stroke-width="2.6" stroke-linecap="round" stroke-linejoin="round" style="flex-shrink:0">' +
             '<path d="M20 6L9 17l-5-5"/></svg>';

  /* PAGE triggers only. The widget's own markup contains two buttons that call
     cuurtOpenQuiz — the panel's "Find my Perfect Size" CTA and the result
     card's "Retake quiz" — and repainting those was wrong twice over: it
     restated the size directly under the Select button that already says it,
     and it destroyed the Retake label the customer needs to get back in.
     Anything inside #cuurt-size-root is ours; never touch it. */
  function triggerEls() {
    var root = document.getElementById('cuurt-size-root');
    var out = [];
    Array.prototype.forEach.call(
      document.querySelectorAll('[data-cuurt-trigger], [onclick*="cuurtOpenQuiz"]'),
      function (el) {
        if (root && root.contains(el)) return;          /* our own markup */
        if (el.id && el.id.indexOf('cuurtRetake_') === 0) return;  /* our own link */
        if (out.indexOf(el) < 0) out.push(el);
      });
    return out;
  }

  function retakeLink(el) {
    var id = 'cuurtRetake_' + (el.getAttribute('data-cuurt-id') || '');
    return document.getElementById(id);
  }

  function paintTriggers(display) {
    triggerEls().forEach(function (el, i) {
      if (!el.getAttribute('data-cuurt-id')) el.setAttribute('data-cuurt-id', String(i));
      if (el.getAttribute('data-cuurt-orig') == null) {
        el.setAttribute('data-cuurt-orig', el.innerHTML);
      }
      /* Tick + the size, nothing else. A "Your CUURT size" kicker above it just
         restates what the number already says. */
      el.innerHTML = TICK + '<span style="font-size:15px;font-weight:700;">' + display + '</span>';

      /* "Retake quiz" goes directly beneath the button, once. */
      var id = 'cuurtRetake_' + el.getAttribute('data-cuurt-id');
      if (!document.getElementById(id) && el.parentNode) {
        var a = document.createElement('button');
        a.type = 'button'; a.id = id;
        a.setAttribute('onclick', 'cuurtOpenQuiz()');
        a.style.cssText = 'display:block;margin:8px auto 0;background:none;border:none;cursor:pointer;' +
          'font-family:inherit;font-size:12.5px;font-weight:600;color:#6b7280;text-decoration:underline;' +
          'text-underline-offset:2px;padding:2px 4px;';
        a.textContent = 'Retake quiz';
        el.parentNode.insertBefore(a, el.nextSibling);
      }
    });
  }

  function clearTriggers() {
    triggerEls().forEach(function (el) {
      var o = el.getAttribute('data-cuurt-orig');
      if (o != null) el.innerHTML = o;
      var r = retakeLink(el);
      if (r && r.parentNode) r.parentNode.removeChild(r);
    });
    try { localStorage.removeItem('cuurt_rec_display'); } catch (e) {}
  }

  /* Called by renderResult the moment a size is recommended. */
  window.cuurtPaintTriggers = function (display) {
    if (!display) return;
    try { localStorage.setItem('cuurt_rec_display', display); } catch (e) {}
    paintTriggers(display);
  };
  window.cuurtClearResult = clearTriggers;

  /* Survive a reload. */
  function restoreTriggers() {
    try {
      var d = localStorage.getItem('cuurt_rec_display');
      if (d) paintTriggers(d);
    } catch (e) {}
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', restoreTriggers);
  else restoreTriggers();

  /* ╔═══════════════════════════════════════════════════════════════════════╗
     ║  SELF-TEST — run window.__cuurtEngineTest() in the browser console.    ║
     ║  Asserts the never-short rule across every brand, gender, size, fit,   ║
     ║  width and toe-room combination. Should always print 0 violations.     ║
     ╚═══════════════════════════════════════════════════════════════════════╝ */
  window.__cuurtCalc = function (a) { return recommend(Object.assign(blank(), a || {})); };

  window.__cuurtEngineTest = function () {
    var v = [], n = 0;
    var GAPS = [1, 2, 3, 4, 5];
    var WIDTHS = ['narrow', 'normal', 'wide', 'very-wide'];
    var TOES = ['snug', 'natural', 'extra'];

    function assert(label, a) {
      var r;
      try { r = window.__cuurtCalc(a); }
      catch (e) { v.push({ label: label, check: 'THREW', msg: e.message }); return; }
      if (!r) { v.push({ label: label, check: 'NULL' }); return; }
      n++;
      /* THE floor — the one rule that must never bend */
      if (!r.capped && r.clearance < TUNE.MIN_CLEARANCE_MM - 0.01) {
        v.push({ label: label, check: 'BELOW-FLOOR', clearance: +r.clearance.toFixed(2) });
      }
      /* width may never move the pick by more than one size */
      if (r.widthExtra > 0) {
        var gap = Math.abs(LADDER.indexOf(r.size) - LADDER.indexOf(r.lengthOnlySize));
        if (gap > 1) v.push({ label: label, check: 'WIDTH>1SIZE', gap: gap });
      }
      /* The engine's own pick never sits below TARGET − half a step — unless
         no longer size was actually available (top of the ladder, or the next
         size would breach the max). Those are constraints, not mistakes. */
      if (!r.capped && !r.runsRoomy && !r.widthChangedSize &&
          r.clearance < r.target - TUNE.SIZE_STEP_MM / 2 - 0.01) {
        var upSize = LADDER[LADDER.indexOf(r.size) + 1];
        var upEligible = upSize && (upSize.len - r.foot) <=
          TUNE.MAX_CLEARANCE_MM + Math.max(0, TUNE.TOE_PREFERENCE_DELTA_MM[a.toe] || 0) + 0.01;
        if (upEligible) v.push({ label: label, check: 'SHOULD-HAVE-SIZED-UP', clearance: +r.clearance.toFixed(2) });
      }
    }

    Object.keys(BDEU).forEach(function (brand) {
      var t = BDEU[brand];
      ['u', 'men', 'women'].forEach(function (key) {
        if (!t[key]) return;
        var gender = key === 'women' ? 'women' : 'men';
        t[key].forEach(function (row) {
          GAPS.forEach(function (q) { WIDTHS.forEach(function (width) { TOES.forEach(function (toe) {
            [true, false].forEach(function (suw) {
              assert(brand + '/' + gender + '/EU' + row[0] + '/gap' + q + '/' + width + '/' + toe, {
                brand: brand, gender: gender, toeGap: q, sizedUpForWidth: suw,
                width: width, toe: toe, conditions: ['none'],
                euSel: { label: row[0], num: row[1], mm: row[2] }
              });
            });
          }); }); });
        });
      });
    });
    Object.keys(BD).forEach(function (brand) {
      ['men', 'women'].forEach(function (gender) {
        var tbl = BD[brand][gender]; if (!tbl) return;
        Object.keys(tbl).map(Number).forEach(function (sz) {
          GAPS.forEach(function (q) { WIDTHS.forEach(function (width) {
            assert(brand + '/' + gender + '/US' + sz + '/gap' + q + '/' + width, {
              brand: brand, gender: gender, toeGap: q, width: width, toe: 'natural',
              conditions: ['none'], usSize: sz
            });
          }); });
        });
      });
    });
    for (var mm = 200; mm <= 330; mm++) {
      WIDTHS.forEach(function (width) { TOES.forEach(function (toe) {
        assert('measure/' + mm + '/' + width + '/' + toe,
          { footMm: mm, width: width, toe: toe, gender: 'men', conditions: ['none'] });
      }); });
    }

    if (v.length && console.table) console.table(v); else if (v.length) console.log(v);
    console.log('__cuurtEngineTest: ' + n + ' cases, ' + v.length + ' violations' +
                (v.length ? '  ✗' : '  ✓ floor holds, width bounded'));
    return { checked: n, violations: v };
  };

  /* ── SELF-INSTALL ────────────────────────────────────────────────────────
     If the modal markup isn't on the page, put it there.

     Why: cuurtOpenQuiz() can only open markup that exists. On a page where the
     Liquid snippet isn't rendered, a trigger button used to do NOTHING — no
     error, no message, just a dead click. The tell was that the quiz "only
     worked where the size chart worked", because the chart is part of the same
     snippet: its presence was evidence the snippet had rendered, never a
     dependency of the quiz.

     The modal is position:fixed, so appending it to <body> is equivalent to
     rendering it anywhere else. This makes the ASSET self-sufficient: load
     cuurt-size-recommender.js on any page and every trigger works, snippet or
     no snippet.

     Additive only — if #cuurt-size-root is already present (the snippet did
     render) this does nothing and that copy is used as-is. */
  (function selfInstall() {
    if (document.getElementById('cuurt-size-root')) return;
    var host = document.createElement('div');
    host.innerHTML = CUURT_MODAL_HTML;
    while (host.firstChild) document.body.appendChild(host.firstChild);
  })();

  /* ── DUPLICATE-RENDER GUARD ──────────────────────────────────────────────
     The Liquid snippet carries an {%- unless -%} guard, but {% render %} gives
     each snippet an ISOLATED scope: an {%- assign -%} inside it does not
     survive to the next render. So the Liquid guard alone cannot stop a second
     copy of the markup, and two copies means duplicate element ids — every
     getElementById below would silently address the first one while the
     customer looked at the second, and the quiz would appear frozen.

     Cheapest reliable fix: at load, keep the FIRST #cuurt-size-root and remove
     any others. Runs before the customer can click anything. It also covers the
     case where an older copy of this widget is still rendered elsewhere on the
     page under the same ids. */
  (function dedupe() {
    var roots = document.querySelectorAll('#cuurt-size-root');
    for (var i = 1; i < roots.length; i++) {
      roots[i].parentNode.removeChild(roots[i]);
    }
    if (roots.length > 1) {
      console.warn('[CUURT] Removed ' + (roots.length - 1) + ' duplicate copy/copies of the ' +
        'size finder markup. Render the snippet ONCE per page — triggers are just ' +
        'onclick="cuurtOpenQuiz()", they do not need their own copy of the modal.');
    }
  })();

  /* ── KEYBOARD / OVERLAY ──────────────────────────────────── */
  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape') { cuurtClosePanel(); cuurtCloseQuiz(); }
    if (e.key === 'Enter' && step === 'measure') { window.cuurtSubmitFoot(); }
  });
  document.addEventListener('click', function (e) {
    var o = document.getElementById('cuurtQuizOverlay');
    if (o && e.target === o) cuurtCloseQuiz();
  });

})();